import React, { Component } from "react";
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col, Modal } from "react-bootstrap";
import { components } from "react-select";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TabSection from "./TabSection";
import SelectDropDown from "../selectdropdown/SelectDropDown";
import MultiSelect from "react-multi-select-component";
import ProfileDateBlock from "./ProfileDateBlock";
import CardLessDataTableWithSearch from "../datatables/CardLessDataTableWithSearch";
import { PageShimmer } from "./Shimmer";
import { pointTooltip } from "./chartTooltip";
import ChartBoundary from "./ChartBoundary";
import { getEffectiveBranchIds, onBranchScopeChange } from "../../../utils/branchScope";

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
// The student-app tab keys on mobile.
const resolveMobile = (student) => {
    if (!student) return null;
    return (
        student.contact_no || student.student_mobile || student.mobile ||
        student.studentMobile || student.parent_mobile || student.phone ||
        student.mobile_no || student.username || null
    );
};

// College view: this profile shows ANOTHER student, so "You/Your" becomes the
// student's (first) name. Falls back to "Student" if no name.
const studentName = (student) => {
    const full = (student && (student.student_name || student.name)) || "";
    return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
            <i className="fal fa-angle-down" />
        </components.DropdownIndicator>
    );

// Rich batch leaderboard (getBatchTopStudents) — same fields as the student app,
// scoped to the viewed student's batch via their mobile.
const GET_TOP_STUDENTS = gql`
  query ($institute_id: Int!, $mobile: String, $page: Int, $limit: Int, $exam_stream: Int) {
    getBatchTopStudents(institute_id: $institute_id, mobile: $mobile, page: $page, limit: $limit, exam_stream: $exam_stream) {
      rank
      branch_id
      section_id
      class_id
      name
      mobile
      exam_id
      exam_names
      exam_name
      class
      section
      branch
      total_exams
      total_correct
      total_wrong
      total_questions
      avg_percentage
      avg
      total_not_answered
      total_skipped
    }
  }
`;

const examTypes = [
    { value: 0, label: "All Exams" },
    { value: 1, label: "Mock Test" },
    { value: 2, label: "Custom Exam" },
    { value: 3, label: "Scheduled Exam" },
    { value: 4, label: "Error Exam" },
    { value: 6, label: "Practice Test" },
    { value: 7, label: "Adaptive Test" },
];

const GET_STUDENT_PERFORMANCE_REPORT = gql`
  query (
    $mobile: String
    $student_user_id: Int
    $edu_student_id: Int
    $exam_type: Int
    $exam_stream: Int
    $fromDate: String
    $toDate: String
    $target: String
  ) {
    getStudentPerformanceReport(
      mobile: $mobile
      student_user_id: $student_user_id
      edu_student_id: $edu_student_id
      exam_type: $exam_type
      exam_stream: $exam_stream
      fromDate: $fromDate
      toDate: $toDate
    ) {
      snapshot(target: $target) {
        attendance
        best_subject
        weak_subject
        target
        predicted_score
        max_score
        predicted_label
        simulated_air
        risk_level
      }
      overall {
        exam_name
        total_attempt_test
        average_score
        highest_score
        lowest_score
        section_avg_score
        branch_avg_score
        avg_rank
        accuracy
      }
      scoreTrend {
        exam_name
        label
        score
        total_marks
        batch_avg
        batch_size
        topper_score
        topper_name
      }
                      examWiseResult {
                            id
                            exam_name
                            date
                            marks
                            accuracy
                            exam_rank
                            state_rank
                            city_rank
                            branch_rank
                            section_rank
                            section_total
                            mode
                            correct
                            wrong
                            skipped
                            not_answered
                            total_questions
                            correct_marks
                            negative_marks
                            total_marks
                            over_time
                            less_time
                        }
             subjectPerformance {
                            subject_id
                            subject
                            best_score
                            lowest_score
                            section_avg
                            branch_avg
                            accuracy
                        }
      chapterPerformance {
        subject_id
        subject
        chapter_id
        chapter
        accuracy_percentage
      }
      topicPerformance {
        subject_id
        subject
        chapter_id
        chapter
        topic_id
        topic
        attempted
        accuracy_percentage
      }
      examSubjectMatrix {
        exam_paper_id
        session_id
        exam_name
        date
        subjects {
          subject_id
          subject
          attempted
          accuracy
          score
        }
      }
    }
  }
`;

const GET_BATCH_SCORE_COMPARISON = gql`
  query ($institution_id: Int, $branch_id: String, $mobile: String) {
    getBatchScoreComparison(institution_id: $institution_id, branch_id: $branch_id, mobile: $mobile) {
      section_id
      section_name
      branch_id
      branch_name
      student_count
      subjects {
        subject_id
        subject
        avg_accuracy
        student_count
      }
      chapters {
        subject_id
        subject
        chapter_id
        chapter
        avg_accuracy
      }
    }
  }
`;

// Full syllabus chapters for the student's subjects (all chapters, attempted or not).
// College backend takes student_user_id / edu_student_id (not mobile).
const GET_STUDENT_SYLLABUS = gql`
  query ($student_user_id: Int, $edu_student_id: Int) {
    getStudentSyllabusChapters(student_user_id: $student_user_id, edu_student_id: $edu_student_id) {
      subject_id
      subject
      chapter_id
      chapter
      topic_count
    }
  }
`;

// Exam history rows — same query the /student/subject/exam-history page uses.
const GET_STUDENT_EXAM_SESSIONS = gql`
  query ($mobile: String!, $exam_session_id: ID) {
    getStudentExamSessions(mobile: $mobile, exam_session_id: $exam_session_id) {
      id
      correct
      wrong
      not_answered
      total_questions
      correct_marks
      negative_marks
      total_marks
      type
      sub_type
      timestamp
      accuracy
      exam_name
      speed
    }
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Truncate a label to N chars with an ellipsis (full text kept in the tooltip).
const trunc = (s, n = 15) => {
    const str = `${s == null ? "" : s}`;
    return str.length > n ? `${str.slice(0, n)}...` : str;
};

// Section (batch) bar colors for the comparison chart.
const BATCH_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#0ea5e9", "#a855f7", "#ec4899"];

// Accuracy → heatmap cell color (red = weak → green = strong).
const heatColor = (pct) => {
    if (pct == null || isNaN(pct)) return "#f1f5f9";
    if (pct < 40) return "#ef4444";
    if (pct < 55) return "#f97316";
    if (pct < 70) return "#f59e0b";
    if (pct < 85) return "#84cc16";
    return "#22c55e";
};

// Snapshot prediction target — derive from the VIEWED student's exam_id, which is
// the authoritative signal (2 = JEE, 1 = NEET), matching resolveStudentStream.
// This avoids the earlier bug where a cookie/name substring check defaulted every
// non-"neet" student (blank/BIPC/Medical labels, or the viewer's cookie on a
// profile page) to JEE — scoring NEET students on the JEE curve. Only fall back to
// the name text when exam_id is absent.
const resolveTarget = (student) => {
    const examId = num(student?.exam_id);
    if (examId === 1) return "neet";
    if (examId === 2) return "jee";
    const exam = `${student?.exam_name || student?.stream_name || student?.exam || Cookies.get("exam_name") || ""}`.toLowerCase();
    if (exam.includes("neet") || exam.includes("bipc") || exam.includes("medical")) return "neet";
    return "jee";
};

// MPC / BIPC subject-id groups (Botany=1, Physics=2, Chemistry=3, Maths=4, Zoology=5).
const STREAM_SUBJECT_IDS = {
    MPC: new Set([2, 3, 4]),
    BIPC: new Set([1, 2, 3, 5]),
};

// Stream-DEFINING subjects (Physics/Chemistry are shared, so they don't define a stream).
// A section qualifies for a stream only if it carries one of these.
const STREAM_DEFINING_IDS = {
    MPC: new Set([4]), // Maths
    BIPC: new Set([1, 5]), // Botany, Zoology
};

// Infer a section's stream from the subjects it offers (Botany/Zoology => BIPC, Maths => MPC).
const sectionStream = (sec) => {
    const ids = new Set((sec?.subjects || []).map((s) => num(s.subject_id)));
    if (ids.has(1) || ids.has(5)) return "BIPC";
    if (ids.has(4)) return "MPC";
    return null;
};

// Resolve the student's stream from their profile text, falling back to their own subjects.
const resolveStudentStream = (student, subjectPerformance = []) => {
    // PRIMARY signal: the student's exam program (reliable even when the filtered
    // report is empty). exam_id 2 = JEE → MPC (Phy/Chem/Maths); 1 = NEET → BIPC.
    const examId = num(student?.exam_id);
    if (examId === 2) return "MPC";
    if (examId === 1) return "BIPC";
    // Secondary: text hints (BIPC/MPC/JEE/NEET) in the student's profile fields.
    const text = `${student?.exam_name || ""} ${student?.stream_name || ""} ${student?.stream || ""} ${student?.group || ""} ${student?.group_name || ""} ${student?.section_name || ""} ${student?.class_name || ""} ${student?.class || ""}`.toLowerCase();
    if (text.includes("bipc") || text.includes("neet")) return "BIPC";
    if (text.includes("mpc") || text.includes("jee")) return "MPC";
    // Last resort: infer from which subjects the student actually has data in.
    const ids = new Set(subjectPerformance.map((s) => num(s.subject_id)));
    if (ids.has(1) || ids.has(5)) return "BIPC";
    if (ids.has(4)) return "MPC";
    return null;
};

// Summary cards are aggregated across the overall rows the API returns.
const buildSummary = (overall = []) => {
    if (!overall.length) {
        return { avgScore: 0, highestScore: 0, accuracy: 0, avgRank: 0 };
    }
    const avgScore = overall.reduce((s, r) => s + num(r.average_score), 0) / overall.length;
    const highestScore = Math.max(...overall.map((r) => num(r.highest_score)));
    const accuracy = overall.reduce((s, r) => s + num(r.accuracy), 0) / overall.length;
    const avgRank = overall.reduce((s, r) => s + num(r.avg_rank), 0) / overall.length;
    return {
        avgScore: Math.round(avgScore),
        highestScore: Math.round(highestScore),
        accuracy: Math.round(accuracy),
        avgRank: Math.round(avgRank),
    };
};

// Parse an exam date (handles common day/month/ISO orderings) into a sortable number.
const parseExamDate = (d) => {
    if (!d) return 0;
    const m = moment(d, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601], true);
    return m.isValid() ? m.valueOf() : 0;
};

// Pick the exam paper with the latest date (falls back to the last array item).
const pickLatestExam = (matrix = []) => {
    if (!matrix || !matrix.length) return null;
    return matrix.reduce((best, e) => (parseExamDate(e.date) >= parseExamDate(best.date) ? e : best), matrix[0]);
};

// Per-exam aggregates from the exam-subject matrix (score/accuracy/attempted).
const aggregateExams = (matrix = []) =>
    (matrix || []).map((m) => {
        const subs = m.subjects || [];
        // Round to 2 decimals to kill floating-point artifacts (e.g. 63.010000000000005 → 63.01).
        const round2 = (v) => Math.round(v * 100) / 100;
        const totalScore = round2(subs.reduce((s, x) => s + num(x.score), 0));
        const totalAttempted = subs.reduce((s, x) => s + num(x.attempted), 0);
        const avgAccuracy = subs.length ? Math.round(subs.reduce((s, x) => s + num(x.accuracy), 0) / subs.length) : 0;
        const avgScore = subs.length ? Math.round(totalScore / subs.length) : 0;
        const highestScore = round2(subs.reduce((mx, x) => Math.max(mx, num(x.score)), 0));
        const topSubject = subs.reduce((b, x) => (num(x.score) > num(b?.score ?? -Infinity) ? x : b), null);
        return { ...m, subjects: subs, totalScore, totalAttempted, avgAccuracy, avgScore, highestScore, topSubject };
    });

// Summary cards. Total Score = the student's HIGHEST-SCORING exam (with the "From
// <exam>" sub-line). Avg Score & Avg Accuracy = TRUE averages ACROSS ALL exams
// (mean of each exam's total / accuracy), not the best exam's per-subject figures —
// so an "Avg" card actually shows an average, not a single exam.
const buildMatrixSummary = (matrix = [], overallRaw = []) => {
    const exams = aggregateExams(matrix).filter((e) => e.subjects.length > 0);
    if (!exams.length) return { ...buildSummary(overallRaw), totalScore: 0, highestExamName: null, highestExamDate: null, examCount: 0 };
    const highestExam = exams.reduce((b, e) => (e.totalScore > (b?.totalScore ?? -Infinity) ? e : b), null);
    const attempted = (overallRaw || []).filter((r) => num(r.total_attempt_test) > 0);
    const avgRank = attempted.length
        ? Math.round(attempted.reduce((s, r) => s + num(r.avg_rank), 0) / attempted.length)
        : 0;
    // Cross-exam averages: mean of each exam's TOTAL score and each exam's accuracy.
    const avgScore = Math.round(exams.reduce((s, e) => s + num(e.totalScore), 0) / exams.length);
    const avgAccuracy = Math.round(exams.reduce((s, e) => s + num(e.avgAccuracy), 0) / exams.length);
    return {
        totalScore: highestExam.totalScore,
        highestScore: highestExam.totalScore,
        avgScore,                 // mean exam total across all exams
        accuracy: avgAccuracy,    // mean accuracy across all exams
        avgRank,
        examCount: exams.length,
        highestExamName: highestExam.exam_name || `Exam ${highestExam.exam_paper_id}`,
        highestExamDate: highestExam.date,
    };
};

class OverviewTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedExamValue: { value: 0, label: "All Exams" },
            startDateCalender: moment(new Date()).subtract(1, "year").startOf("day").unix(),
            endDateCalender: moment(new Date()).endOf("day").unix(),
            overallData: [],
            scoreTrend: [],
            subjectPerformance: [],
            chapterPerformance: [], // student's own per-chapter accuracy (for the "You" heatmap row)
            topicPerformance: [], // student's own per-topic accuracy (for the chapter-click popup)
            syllabusChapters: [], // ALL chapters of the student's subjects (full syllabus)
            chapterTopicModal: null, // { subject, chapter, topics:[...] } when a chapter tile is clicked
            snapshot: null,
            examSessions: [], // exam-history rows (getStudentExamSessions)
            batchComparison: [],
            batchLoading: true, // batch comparison fetches separately (can be slow) → its own loader
            // Section selection only — branch now comes from the header (NavbarOne).
            batchSectionSel: null,
            batchView: "table", // "table" | "heatmap"
            batchSubjectSel: null, // { value, label } — subject filter for the heatmap
            batchDetail: null, // clicked heatmap cell → chapter/topic detail modal
            examWiseResult: [],
            summaryModalOpen: false, // stat card → per-exam breakdown modal
            summaryMetric: null, // the clicked stat card
            summaryView: "graph", // "graph" | "table" — Exam-wise Details view
            examSubjectMatrix: [], // per-exam × per-subject attempted/accuracy/score
            topDrawerOpen: false, // Top Students side drawer
            topStudents: [],
            topLoading: false,
            topExpanded: null, // index of the expanded top-student card
            topDetailView: "table", // "table" | "graph" — expanded card view
            topPage: 1, // Top Students drawer pagination (server-side)
            topHasMore: false, // whether a next page likely exists
            topClassSel: "", // Top Students class filter ("" = all)
            topBranchSel: "", // Top Students branch filter ("" = all)
            topSectionSel: "", // Top Students section filter ("" = all)
            subjectMatrixOpen: false, // Best/Weak subject → exam-subject table modal
            subjectMatrixTitle: "", // "Best Subject" | "Weak Subject"
            subjectMatrixView: "graph", // "graph" | "table" — Exam-wise Subject Breakdown view
            selectedMatrixPaper: null, // { value: exam_paper_id, label: exam_name }
            attemptView: "graph", // "graph" | "table" — Exam Performance section (MOU #4)
            attemptExamSel: { value: 0, label: "All exams" }, // exam-paper filter for that section
            attemptModeSel: "all", // "all" | "online" | "offline" — auto-hidden unless both modes exist
            examPerfModalOpen: false, // "View All" popup for Exam Performance (all filtered exams)
            examPerfModalView: "table", // "graph" | "table" — view inside the View-All popup
            showExamModal: false,
            showAttemptChart: false,
            modalTitle: "",
            modalRows: [],
            selectedExamRow: null,
            loading: false,
        };
    }

    componentDidMount() {
        const { selectedExamValue, startDateCalender, endDateCalender } = this.state;
        this.getReport(selectedExamValue?.value, startDateCalender, endDateCalender);
        this.getBatchComparison();
        this.getSyllabus();
        // Exam History table removed — actions moved into the Exam-wise Results modal.
        // this.getExamSessions();
        // Branch comes from the header (NavbarOne). The batch comparison derives its
        // branch scope at render time, so a re-render is enough.
        this.offBranchScope = onBranchScopeChange(() => this.forceUpdate());
    }

    componentWillUnmount() {
        if (this.offBranchScope) this.offBranchScope();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.examStream !== this.props.examStream) {
            const { selectedExamValue, startDateCalender, endDateCalender } = this.state;
            // Reset the Exam-Paper selection to "All exams" — a paper from the old
            // stream (e.g. a Mains exam) won't exist in the new stream, which would
            // filter the Exam Performance section to nothing ("No attempted exams").
            this.setState({ attemptExamSel: { value: 0, label: "All exams" } });
            this.getReport(selectedExamValue?.value, startDateCalender, endDateCalender);
            // Batch comparison must also refresh for the new stream.
            this.getBatchComparison();
            // Re-fetch top students only when the drawer is open (fetched lazily otherwise).
            if (this.state.topDrawerOpen) {
                this.fetchTopStudents(this.state.topPage || 1);
            }
        }
    }

    // Exam-history table data (mirrors the /student/subject/exam-history page query).
    getExamSessions = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_STUDENT_EXAM_SESSIONS,
                variables: { mobile: `${resolveMobile(this.props.student) || ""}`, exam_session_id: 0 },
                fetchPolicy: "network-only",
            });
            this.setState({ examSessions: result?.data?.getStudentExamSessions || [] });
        } catch (error) {
            console.log(error);
            this.setState({ examSessions: [] });
        }
    };

    // Redirect to the exam-result analysis page. examType defaults to the row's
    // own type; the exam-wise modal passes "schedule_exam" explicitly.
    goToExamResult = (row, examType) => {
        if (!row?.id) return;
        this.props.history.push({
            pathname: "/student/subject/exam-result",
            state: { htype: "history", sessionid: row.id, examtype: examType || row.type },
        });
    };

    // Redirect to the question-answer view for the attempt.
    goToViewQA = (row, examType) => {
        if (!row?.id) return;
        this.props.history.push({
            pathname: "/student/view-question-answer",
            state: { htype: "history", sessionid: row.id, examtype: examType || row.type },
        });
    };

    fetchBatchComparison = async (branchId, examPaperId) => {
        const result = await this.props.client.query({
            query: GET_BATCH_SCORE_COMPARISON,
            variables: {
                institution_id: parseInt(Cookies.get("institutionid") || Cookies.get("institution_id")) || 0,
                branch_id: branchId,
                mobile: `${resolveMobile(this.props.student) || ""}`,
            },
            fetchPolicy: "network-only",
        });
        return result?.data?.getBatchScoreComparison || [];
    };

    // examPaperId (schedule_exam_id) → scope the batch to a single exam; null = all.
    // Full syllabus (all chapters of the student's subjects) — used as the master
    // chapter list so the heatmap/table show every chapter, attempted or not.
    getSyllabus = async () => {
        try {
            const res = await this.props.client.query({
                query: GET_STUDENT_SYLLABUS,
                variables: {
                    student_user_id: parseInt(this.props.student?.student_user_id) || 0,
                    edu_student_id: parseInt(this.props.student?.edu_student_id) || 0,
                },
                fetchPolicy: "network-only",
            });
            this.setState({ syllabusChapters: res?.data?.getStudentSyllabusChapters || [] });
        } catch (e) {
            console.log(e);
            this.setState({ syllabusChapters: [] });
        }
    };

    getBatchComparison = async (examPaperId = null) => {
        const { student } = this.props;
        this.setState({ batchLoading: true });
        try {
            // Compare across all branches in the institution (multiple branches exist).
            let data = await this.fetchBatchComparison("", examPaperId);
            // Fallback: some backends return nothing for an empty branch_id — retry with the student's branch.
            if ((!data || data.length === 0) && student?.branch != null) {
                data = await this.fetchBatchComparison(String(student.branch), examPaperId);
            }
            this.setState({ batchComparison: data || [], batchLoading: false });
        } catch (error) {
            console.log(error);
            this.setState({ batchComparison: [], batchLoading: false });
        }
    };

    // Header Exam-Paper dropdown change: scope the whole tab (client-side) AND
    // refetch the batch comparison for that exam (backend needs its paper id).
    handleExamPaperChange = (name, value, opt) => {
        this.setState({ attemptExamSel: opt }, () => {
            this.getBatchComparison(this.selectedExamPaperId());
        });
    };

    // Map the header exam-paper selection (a session id from examWiseResult) to
    // its schedule_exam_id (exam_paper_id) via examSubjectMatrix.
    selectedExamPaperId = () => {
        const sel = this.state.attemptExamSel;
        if (!sel || !sel.value) return null;
        const ex = (this.state.examWiseResult || []).find((e) => e.id === sel.value);
        if (!ex) return null;
        const m = (this.state.examSubjectMatrix || []).find(
            (x) => x.exam_name === ex.exam_name || String(x.session_id) === String(ex.id));
        return m && m.exam_paper_id ? Number(m.exam_paper_id) : null;
    };

    selecthandleInputChange = (name, value, selectedOption) => {
        this.setState({ [name]: selectedOption }, () => {
            this.getReport(selectedOption?.value, this.state.startDateCalender, this.state.endDateCalender);
        });
    };

    applyCallback = (startDate, endDate) => {
        const start = startDate.clone().startOf("day").unix();
        const end = endDate.clone().endOf("day").unix();
        this.setState({ startDateCalender: start, endDateCalender: end }, () => {
            this.getReport(this.state.selectedExamValue?.value, start, end);
        });
    };

    getReport = async (examType, fromDate, toDate) => {
        const fromDateFinal = fromDate || this.state.startDateCalender;
        const toDateFinal = toDate || this.state.endDateCalender;
        const examTypeFinal = examType ?? this.state.selectedExamValue?.value;
        const { student } = this.props;

        this.setState({ loading: true });
        try {
            const result = await this.props.client.query({
                query: GET_STUDENT_PERFORMANCE_REPORT,
                variables: {
                    mobile: `${resolveMobile(this.props.student) || ""}`,
                    // Backstop: college identity so the backend resolves mobile even
                    // when the passed student object has no mobile field.
                    student_user_id: parseInt(this.props.student?.student_user_id) || 0,
                    edu_student_id: parseInt(this.props.student?.edu_student_id) || 0,
                    exam_type: examTypeFinal,
                    exam_stream: this.props.examStream != null ? Number(this.props.examStream) : null,
                    fromDate: fromDateFinal.toString(),
                    toDate: toDateFinal.toString(),
                    target: resolveTarget(student),
                },
                fetchPolicy: "network-only",
            });
            const data = result?.data?.getStudentPerformanceReport;
            const matrix = data?.examSubjectMatrix || [];
            this.setState({
                overallData: data?.overall || [],
                scoreTrend: data?.scoreTrend || [],
                subjectPerformance: data?.subjectPerformance || [],
                chapterPerformance: data?.chapterPerformance || [],
                topicPerformance: data?.topicPerformance || [],
                snapshot: data?.snapshot || null,
                examWiseResult: data?.examWiseResult || [],
                examSubjectMatrix: matrix,
                // Default the modal's exam-paper selection to the last exam.
                selectedMatrixPaper: this.state.selectedMatrixPaper ||
                    (() => {
                        const latest = pickLatestExam(matrix);
                        return latest ? { value: latest.exam_paper_id, label: latest.exam_name } : null;
                    })(),
                loading: false,
            });
        } catch (error) {
            console.log(error);
            this.setState({ overallData: [], scoreTrend: [], subjectPerformance: [], snapshot: null, examWiseResult: [], examSubjectMatrix: [], loading: false });
        }
    };

    formatValue = (value) => (value === null || value === undefined ? "-" : value);

    // Normalise a raw exam row into the display shape used by the modal table.
    mapExamRow = (r) => ({
        id: r.id,
        exam_name: r.exam_name,
        date: r.date,
        status: r.status,
        attempted: r.attempted,
        marks: r.marks,
        accuracy: r.accuracy,
        exam_rank: r.exam_rank,
        state_rank: r.state_rank,
        city_rank: r.city_rank,
        branch_rank: r.branch_rank,
        section_rank: r.section_rank,
        correct: Number(r.correct) || 0,
        wrong: Number(r.wrong) || 0,
        skipped: Number(r.skipped ?? r.not_answered) || 0,
        not_answered: Number(r.not_answered) || 0,
        total_questions: Number(r.total_questions) || 0,
        correct_marks: Number(r.correct_marks) || 0,
        negative_marks: Number(r.negative_marks) || 0,
        total_marks: Number(r.total_marks) || 0,
        over_time: Number(r.over_time) || 0,
        less_time: Number(r.less_time) || 0,
    });

    // Clicking the attempt count opens a modal listing that exam's individual results.
    openExamModal = (row) => {
        const { examWiseResult } = this.state;
        // Only attempted exams are shown in the results list.
        const attemptedOnly = examWiseResult.filter((e) => e.attempted);
        const filtered = attemptedOnly.filter((e) => e.exam_name === row.exam_name);
        // Fall back to all attempted results when the row's exam name doesn't match (e.g. aggregate rows).
        const source = filtered.length ? filtered : attemptedOnly;
        this.setState({
            showExamModal: true,
            showAttemptChart: false,
            modalTitle: row.exam_name || "Exam Results",
            modalRows: source.map(this.mapExamRow),
        });
    };

    // Clickable exam name in the modal — reveals the attempt-rate chart for THAT
    // exam only (matched by exam_paper_id / id, falling back to the row index).
    examNameFormatter = (cell, row, rowIndex) => (
        <button
            type="button"
            className="btn btn-link p-0 text-left"
            style={{ fontWeight: 600, color: "#4f46e5", textDecoration: "underline" }}
            onClick={() => this.setState({ showAttemptChart: true, selectedExamRow: { ...row, __index: rowIndex } })}
        >
            {this.formatValue(cell)}
        </button>
    );

    modalColumns = [
        { dataField: "index", text: "#", formatter: (cell, row, rowIndex) => rowIndex + 1 },
        { dataField: "exam_name", text: "Exam Name", sort: true, formatter: this.examNameFormatter },
        { dataField: "date", text: "Date", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "status", text: "Status", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "marks", text: "Marks", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "accuracy", text: "Accuracy", sort: true, formatter: (cell) => (cell != null ? `${cell}%` : "-") },
        { dataField: "exam_rank", text: "Exam Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "state_rank", text: "State Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "city_rank", text: "City Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "branch_rank", text: "Branch Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "section_rank", text: "Section Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "correct", text: "Correct", sort: true },
        { dataField: "wrong", text: "Wrong", sort: true },
        { dataField: "skipped", text: "Skipped", sort: true },
        { dataField: "not_answered", text: "Not Answered", sort: true },
        { dataField: "total_questions", text: "Total Questions", sort: true },
        { dataField: "correct_marks", text: "Correct Marks", sort: true },
        { dataField: "negative_marks", text: "Negative Marks", sort: true },
        { dataField: "total_marks", text: "Total Marks", sort: true },
        { dataField: "over_time", text: "Over Time", sort: true },
        { dataField: "less_time", text: "Less Time", sort: true },
        {
            dataField: "actions",
            text: "Actions",
            headerAlign: "right",
            align: "right",
            formatter: (cell, row) => (
                <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, borderRadius: 8, padding: 0 }}
                        onClick={() => this.goToExamResult(row, "schedule_exam")}
                        title="View result analysis"
                    >
                        <i className="fas fa-chart-line" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, borderRadius: 8, padding: 0 }}
                        onClick={() => this.goToViewQA(row, "schedule_exam")}
                        title="View questions &amp; answers"
                    >
                        <i className="fas fa-external-link-alt" />
                    </button>
                </div>
            ),
        },
    ];

    closeExamModal = () =>
        this.setState({ showExamModal: false, showAttemptChart: false, modalRows: [], modalTitle: "", selectedExamRow: null });

    // Attempt-count cell rendered as a clickable link.
    attemptFormatter = (cell, row) => {
        const count = this.formatValue(cell);
        if (cell === null || cell === undefined || num(cell) === 0) return count;
        return (
            <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontWeight: 600, color: "#4f46e5", textDecoration: "underline" }}
                onClick={() => this.openExamModal(row)}
            >
                {count}
            </button>
        );
    };

    articlecolumns = [
        { dataField: "index", text: "#", formatter: (cell, row, rowIndex) => rowIndex + 1 },
        { dataField: "exam_name", text: "Exam Name", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "total_attempt_test", text: "Total Attempt Test", sort: true, formatter: this.attemptFormatter },
        { dataField: "average_score", text: "Average Score", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "highest_score", text: "Highest Score", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "lowest_score", text: "Lowest Score", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "section_avg_score", text: "Section Avg Score", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "branch_avg_score", text: "Branch Avg Score", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "avg_rank", text: "Average Rank", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "accuracy", text: "Accuracy Avg", sort: true, formatter: (cell) => this.formatValue(cell) },
    ];

    // Columns for the exam-history table (getStudentExamSessions).
    examHistoryColumns = [
        { dataField: "index", text: "#", formatter: (cell, row, rowIndex) => rowIndex + 1 },
        {
            dataField: "exam_name",
            text: "Exam Name",
            sort: true,
            formatter: (cell, row) => (
                <button
                    type="button"
                    className="btn btn-link p-0 text-left"
                    style={{ fontWeight: 600, color: "#2563eb", textDecoration: "none" }}
                    onClick={() => this.goToExamResult(row)}
                    title="View exam result"
                >
                    {this.formatValue(cell)}
                </button>
            ),
        },
        {
            dataField: "timestamp",
            text: "Date",
            sort: true,
            formatter: (cell) => {
                if (!cell) return "-";
                const d = moment(isNaN(cell) ? cell : new Date(parseInt(cell) * 1000));
                return d.isValid() ? d.format("DD MMM YYYY") : "-";
            },
        },
        { dataField: "total_questions", text: "Questions", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "correct", text: "Correct", sort: true, formatter: (cell) => <span style={{ color: "#16a34a", fontWeight: 600 }}>{this.formatValue(cell)}</span> },
        { dataField: "wrong", text: "Wrong", sort: true, formatter: (cell) => <span style={{ color: "#ef4444", fontWeight: 600 }}>{this.formatValue(cell)}</span> },
        { dataField: "not_answered", text: "Not Answered", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "total_marks", text: "Marks", sort: true, formatter: (cell) => <span style={{ fontWeight: 700, color: "#4f46e5" }}>{this.formatValue(cell)}</span> },
        { dataField: "accuracy", text: "Accuracy", sort: true, formatter: (cell) => (cell === null || cell === undefined ? "-" : `${cell}%`) },
        {
            dataField: "actions",
            text: "Actions",
            headerAlign: "right",
            align: "right",
            formatter: (cell, row) => (
                <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, borderRadius: 8, padding: 0 }}
                        onClick={() => this.goToExamResult(row)}
                        title="View result analysis"
                    >
                        <i className="fas fa-chart-line" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, borderRadius: 8, padding: 0 }}
                        onClick={() => this.goToViewQA(row)}
                        title="View questions &amp; answers"
                    >
                        <i className="fas fa-external-link-alt" />
                    </button>
                </div>
            ),
        },
    ];

    renderExamHistoryTable = () => {
        const rows = this.state.examSessions || [];
        return (
            <div
                className="mt-4"
                style={{
                    borderRadius: "16px",
                    border: "1px solid #ececf6",
                    background: "#ffffff",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                }}
            >
                <div
                    className="d-flex align-items-center"
                    style={{ gap: 12, padding: "16px 20px", borderBottom: "1px solid #f1f2f5" }}
                >
                    <div
                        className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 12,
                            background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
                            boxShadow: "0 6px 14px rgba(37,99,235,0.3)",
                        }}
                    >
                        <i className="fas fa-history" style={{ fontSize: 17 }} />
                    </div>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}>
                            Exam History
                        </h6>
                        <span className="text-muted" style={{ fontSize: 12.5 }}>
                            Every exam attempt with score &amp; accuracy
                        </span>
                    </div>
                </div>
                <div style={{ padding: "8px 16px 4px" }}>
                    {rows.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No exam history available.</div>
                    ) : (
                        <CardLessDataTableWithSearch
                            parentData={rows}
                            particlecolumns={this.examHistoryColumns}
                            name="examhistory"
                        />
                    )}
                </div>
            </div>
        );
    };

    renderSummaryCards = (overall, matrix) => {
        // Best Score = the highest-scoring exam; Avg Score / Avg Accuracy = true
        // averages across all exams (see buildMatrixSummary). Scoped to the selected
        // exam when the header exam-paper filter is on a single one.
        const { avgScore, totalScore, accuracy, highestExamName, highestExamDate, examCount } =
            buildMatrixSummary(matrix || this.state.examSubjectMatrix, overall);
        const acrossLabel = examCount ? `across ${examCount} exam${examCount === 1 ? "" : "s"}` : "across exams";
        // Predicted rank comes from the JEE/NEET prediction snapshot.
        const snap = this.state.snapshot || {};
        const predTarget = (snap.target || "").toUpperCase();
        const predRank = snap.simulated_air != null && snap.simulated_air !== "" ? snap.simulated_air : null;
        const cards = [
            {
                label: "Best Score",
                value: totalScore,
                field: "total_score",
                icon: "fa-star",
                iconBg: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                note: "highest exam",
                noteColor: "#4f46e5",
                detail: highestExamName,   // drives the "From <exam>" sub-line
                detailDate: highestExamDate,
            },
            {
                label: "Avg Score",
                value: avgScore,
                field: "average_score",
                icon: "fa-bullseye",
                iconBg: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
                note: acrossLabel,         // true cross-exam average → "across N exams"
                noteColor: "#7c3aed",
                noteIcon: "fa-layer-group",
            },
            {
                label: "Avg Accuracy",
                value: `${accuracy}%`,
                field: "accuracy",
                icon: "fa-check-circle",
                iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                note: acrossLabel,         // true cross-exam average
                noteColor: "#16a34a",
                noteIcon: "fa-layer-group",
            },
            {
                label: predTarget ? `Predicted ${predTarget} Rank` : "Predicted Rank",
                value: predRank != null ? `~${Number(predRank).toLocaleString()}` : "-",
                field: "predicted_rank",
                icon: "fa-medal",
                iconBg: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                note: "predicted",
                noteColor: "#f97316",
                nonClickable: true,
                hint: "Predicted all-India rank in the batch — based on performance, not directly tied to your score.",
            },
        ];

        return (
            <Row className="mb-4">
                {cards.map((m) => (
                    <Col md={3} sm={6} key={m.label} className="mb-3 mb-md-0">
                        <div
                            className="sp-summary-card d-flex align-items-start justify-content-between p-3 h-100"
                            onClick={m.nonClickable ? undefined : () => this.setState({ summaryModalOpen: true, summaryMetric: m, summaryView: "graph" })}
                            title={m.hint || (m.nonClickable ? undefined : `View ${m.label} per exam`)}
                            style={{
                                borderRadius: "16px",
                                background: "#ffffff",
                                border: "1px solid #f1f2f5",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                                cursor: m.nonClickable ? "default" : "pointer",
                            }}
                        >
                            <div>
                                <span className="d-block text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>
                                    {m.label}
                                </span>
                                <strong
                                    className="d-block text-dark"
                                    style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}
                                >
                                    {m.value}
                                </strong>
                                {m.detail ? (
                                    <span className="d-block text-truncate" style={{ fontSize: "12px", fontWeight: 600, color: m.noteColor, maxWidth: 180 }} title={`From ${m.detail}${m.detailDate ? ` · ${m.detailDate}` : ""}`}>
                                        <i className="fas fa-trophy mr-1" style={{ fontSize: 10 }} />From {m.detail}
                                    </span>
                                ) : m.note === "predicted" ? (
                                    <span className="d-block" style={{ fontSize: "12px", fontWeight: 600, color: m.noteColor }}>
                                        <i className="fas fa-info-circle mr-1" style={{ fontSize: 10 }} />predicted
                                    </span>
                                ) : m.note ? (
                                    <span className="d-block" style={{ fontSize: "12px", fontWeight: 600, color: m.noteColor }}>
                                        <i className={`fas ${m.noteIcon || "fa-layer-group"} mr-1`} style={{ fontSize: 10 }} />{m.note}
                                    </span>
                                ) : null}
                            </div>
                            <div
                                className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "12px",
                                    background: m.iconBg,
                                    boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                                }}
                            >
                                <i className={`fas ${m.icon}`} style={{ fontSize: "18px" }}></i>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        );
    };

    // Compact Exam + Date controls rendered in the TabSection header (right side).
    renderControls = () => {
        // Exam-paper options (All + each distinct attempted exam) — drives the
        // Exam Performance section. Rendered AFTER the Exam-type filter.
        const perfRows = this.buildExamPerformanceRows();
        const examPaperOptions = [
            { value: 0, label: "All exams" },
            ...perfRows.map((r) => ({ value: r.id, label: r.exam_name })),
        ];
        return (
        <div className="d-flex flex-wrap align-items-center justify-content-end" style={{ gap: "12px" }}>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>
                    Exam
                </span>
                <div style={{ minWidth: "170px" }}>
                    <SelectDropDown
                        stateData={this.state.selectedExamValue}
                        handleChange={this.selecthandleInputChange}
                        name="selectedExamValue"
                        options={examTypes}
                        placeholderName="Exam"
                        dropdownIndicator={{ DropdownIndicator }}
                    />
                </div>
            </div>
            {examPaperOptions.length > 1 && (
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>
                        Exam Paper
                    </span>
                    <div style={{ minWidth: "190px" }}>
                        <SelectDropDown
                            stateData={this.state.attemptExamSel || examPaperOptions[0]}
                            handleChange={this.handleExamPaperChange}
                            name="attemptExamSel"
                            options={examPaperOptions}
                            placeholderName="Exam Paper"
                            dropdownIndicator={{ DropdownIndicator }}
                        />
                    </div>
                </div>
            )}
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>
                    Date
                </span>
                <ProfileDateBlock
                    applyCallback={this.applyCallback}
                    start={moment.unix(this.state.startDateCalender)}
                    end={moment.unix(this.state.endDateCalender)}
                />
            </div>
            <button type="button" className="sp-ask-doubt-btn" onClick={this.openTopStudents}>
                <i className="fas fa-trophy mr-2" /> Top 10 Students
            </button>
        </div>
        );
    };

    // Fetch top students from the college backend (separate endpoint) and open the drawer.
    TOP_PAGE_SIZE = 10;

    openTopStudents = () => {
        this.setState({ topDrawerOpen: true, topPage: 1, topExpanded: null, topClassSel: "", topBranchSel: "", topSectionSel: "" });
        this.fetchTopStudents(1);
    };

    fetchTopStudents = async (page = 1) => {
        this.setState({ topLoading: true, topExpanded: null });
        try {
            const result = await this.props.client.query({
                query: GET_TOP_STUDENTS,
                variables: {
                    institute_id: parseInt(Cookies.get("institutionid") || Cookies.get("institution_id")) || 0,
                    mobile: `${resolveMobile(this.props.student) || ""}`,
                    exam_stream: this.props.examStream != null ? Number(this.props.examStream) : null,
                    page,
                    limit: this.TOP_PAGE_SIZE,
                },
                fetchPolicy: "network-only",
            });
            const list = result?.data?.getBatchTopStudents || [];
            this.setState({ topStudents: list, topLoading: false, topPage: page, topHasMore: list.length >= this.TOP_PAGE_SIZE });
        } catch (e) {
            console.log(e);
            this.setState({ topStudents: [], topLoading: false, topHasMore: false });
        }
    };

    // Rank medal color (1=gold, 2=silver, 3=bronze).
    rankColor = (rank) => (rank === 1 ? "#f59e0b" : rank === 2 ? "#94a3b8" : rank === 3 ? "#b45309" : "#6366f1");

    // Expanded top-student card — previous exams correct/wrong totals as table + graph.
    renderTopStudentDetail = (s) => {
        const view = this.state.topDetailView || "table";
        const correct = num(s.total_correct);
        const wrong = num(s.total_wrong);
        // const notAnswered = num(s.total_not_answered);
        const skipped = num(s.total_skipped);
        const totalQ = num(s.total_questions) || correct + wrong;
        const unattempted = Math.max(0, totalQ - correct - wrong);
        const exams = `${s.exam_names || ""}`.split(",").map((e) => e.trim()).filter(Boolean);
        const accuracy = totalQ > 0 ? Math.round((correct / totalQ) * 1000) / 10 : 0;

        const setView = (v) => (e) => { e.stopPropagation(); this.setState({ topDetailView: v }); };

        const donut = {
            chart: { type: "pie", backgroundColor: "transparent", height: 210 },
            title: { text: "" },
            credits: { enabled: false },
            tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
            plotOptions: {
                pie: {
                    innerSize: "62%",
                    dataLabels: { enabled: true, format: "{point.name}: {point.y}", style: { fontSize: "11px", fontWeight: "600", color: "#334155" }, distance: 8 },
                },
            },
            series: [{
                name: "Questions",
                data: [
                    { name: "Correct", y: correct, color: "#16a34a" },
                    { name: "Wrong", y: wrong, color: "#ef4444" },
                    ...(unattempted > 0 ? [{ name: "Unattempted", y: unattempted, color: "#cbd5e1" }] : []),
                ],
            }],
        };

        return (
            <div className="px-3 pb-3" style={{ borderTop: "1px dashed #e2e8f0" }} onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
                    <span className="font-weight-bold text-dark" style={{ fontSize: 12.5 }}>Performance Summary — Correct / Wrong</span>
                    <div className="sp-segment d-inline-flex" style={{ fontSize: 11 }}>
                        <button type="button" className={`sp-segment-btn ${view === "table" ? "active" : ""}`} onClick={setView("table")}><i className="fas fa-table mr-1" />Table</button>
                        <button type="button" className={`sp-segment-btn ${view === "graph" ? "active" : ""}`} onClick={setView("graph")}><i className="fas fa-chart-pie mr-1" />Graph</button>
                    </div>
                </div>

                {view === "table" ? (
                    <table className="table table-sm mb-2" style={{ fontSize: 12.5 }}>
                        <tbody>
                            <tr><td className="text-muted">Total Exams</td><td className="text-right font-weight-bold text-dark">{num(s.total_exams)}</td></tr>
                            <tr><td className="text-muted">Correct Answers</td><td className="text-right font-weight-bold" style={{ color: "#16a34a" }}>{correct}</td></tr>
                            <tr><td className="text-muted">Wrong Answers</td><td className="text-right font-weight-bold" style={{ color: "#ef4444" }}>{wrong}</td></tr>
                            {/* <tr><td className="text-muted">Not Answered</td><td className="text-right font-weight-bold" style={{ color: "#f59e0b" }}>{notAnswered}</td></tr> */}
                            <tr><td className="text-muted">Skipped</td><td className="text-right font-weight-bold" style={{ color: "#94a3b8" }}>{skipped}</td></tr>
                            <tr><td className="text-muted">Total Questions</td><td className="text-right font-weight-bold text-dark">{totalQ}</td></tr>
                            <tr><td className="text-muted">Accuracy</td><td className="text-right font-weight-bold" style={{ color: "#6366f1" }}>{accuracy}%</td></tr>
                            <tr><td className="text-muted">Average Score</td><td className="text-right font-weight-bold text-dark">{s.avg != null ? s.avg : `${num(s.avg_percentage)}%`}</td></tr>
                        </tbody>
                    </table>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={donut} />
                )}

                {exams.length > 0 ? (
                    <div className="mt-2">
                        <div className="text-muted mb-1" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>Exams Attempted</div>
                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                            {exams.map((e, k) => (
                                <span key={k} style={{ fontSize: 11, background: "#f1f5f9", color: "#475569", borderRadius: 8, padding: "3px 8px" }}>{e}</span>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    // Sections of the student's own stream + branch, taken from the batch comparison data.
    // Shared between the Batch-wise chart and the Top 10 Students section filter so both
    // list exactly the same sections (all sections in the batch, not just those in the top 10).
    getScopedBatchSections = () => {
        const sections = this.state.batchComparison || [];
        const stream = resolveStudentStream(this.props.student, this.state.subjectPerformance);
        const definingIds = stream ? STREAM_DEFINING_IDS[stream] : null;
        const sectionInStream = (sec) =>
            !definingIds || (sec.subjects || []).some((s) => definingIds.has(num(s.subject_id)));
        const filteredSections = definingIds ? sections.filter(sectionInStream) : sections;
        const streamMatched = !definingIds || filteredSections.length > 0;
        const streamSections = streamMatched ? filteredSections : sections;

        const stu = this.props.student || {};
        const stuBranchKey = stu.branch_id != null ? String(stu.branch_id) : (stu.branch_name ? String(stu.branch_name) : null);
        const branchKey = (sec) => String(sec.branch_id != null ? sec.branch_id : sec.branch_name);
        const branchesInData = new Set(streamSections.map(branchKey));
        // Scope to the student's own branch when it exists in the data; otherwise show all.
        return stuBranchKey && branchesInData.has(stuBranchKey)
            ? streamSections.filter((sec) => branchKey(sec) === stuBranchKey)
            : streamSections;
    };

    renderTopStudentsDrawer = () => {
        const { topDrawerOpen, topStudents, topLoading, topPage, topHasMore, topSectionSel } = this.state;
        const curPage = topPage;

        // Distinct filter options derived from the loaded students.
        const distinct = (key) => Array.from(new Set(topStudents.map((s) => `${s[key] ?? ""}`.trim()).filter(Boolean))).sort();
        // Section options come from the SAME source as the Batch-wise comparison dropdown
        // (all sections of the student's stream + branch), so every section is listed —
        // not just the sections that happen to have a student in the top 10 (e.g. NS 2).
        const batchSectionNames = this.getScopedBatchSections()
            .map((sec) => `${sec.section_name ?? ""}`.trim())
            .filter(Boolean);
        const sectionOpts = Array.from(new Set([...batchSectionNames, ...distinct("section")])).sort();

        // Class & Branch are locked to the logged-in student's own (from cookies); only Section filters.
        const lockClassRaw = `${Cookies.get("class_id") || Cookies.get("classid") || ""}`.trim();
        const lockClassLabel = lockClassRaw === "1" ? "XI" : lockClassRaw === "2" ? "XII" : lockClassRaw;
        const lockBranchLabel = `${Cookies.get("branch_name") || ""}`.trim();

        // Class/branch are already scoped by the query; only the Section selection filters client-side.
        const matches = (s) =>
            (!topSectionSel || `${s.section ?? ""}`.trim() === topSectionSel);
        const pageItems = topStudents.filter(matches);

        const goPage = (p) => this.fetchTopStudents(p);
        const selStyle = { fontSize: 12, padding: "5px 8px", borderRadius: 8, border: "1px solid #e2e8f0", color: "#475569", background: "#fff", flex: 1, minWidth: 0 };
        const lockedSelStyle = { ...selStyle, background: "#f1f5f9", color: "#94a3b8", cursor: "not-allowed" };
        return (
            <>
                <div className={`sp-drawer-overlay ${topDrawerOpen ? "open" : ""}`} onClick={() => this.setState({ topDrawerOpen: false })} />
                <div className={`sp-doubt-drawer ${topDrawerOpen ? "open" : ""}`}>
                    <div className="sp-drawer-header">
                        <h5 className="font-weight-bold text-dark mb-0"><i className="fas fa-trophy mr-2" style={{ color: "#f59e0b" }} />Top 10 Students</h5>
                        <button type="button" className="sp-drawer-close" onClick={() => this.setState({ topDrawerOpen: false })}>&times;</button>
                    </div>
                    <div className="sp-drawer-body">
                        {!topLoading && topStudents.length > 0 ? (
                            <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
                                {lockClassLabel ? (
                                    <select value={lockClassLabel} disabled style={lockedSelStyle}>
                                        <option value={lockClassLabel}>Class {lockClassLabel}</option>
                                    </select>
                                ) : null}
                                {lockBranchLabel ? (
                                    <select value={lockBranchLabel} disabled style={lockedSelStyle}>
                                        <option value={lockBranchLabel}>{lockBranchLabel}</option>
                                    </select>
                                ) : null}
                                <select value={topSectionSel} onChange={(e) => this.setState({ topSectionSel: e.target.value, topExpanded: null })} style={selStyle}>
                                    <option value="">All Sections</option>
                                    {sectionOpts.map((sc) => <option key={sc} value={sc}>{sc}</option>)}
                                </select>
                            </div>
                        ) : null}
                        {topLoading ? (
                            <div className="text-center text-muted py-5"><i className="fas fa-circle-notch fa-spin mr-2" />Loading…</div>
                        ) : topStudents.length > 0 && pageItems.length === 0 ? (
                            <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>No students match the selected filters.</div>
                        ) : topStudents.length > 0 ? (
                            (() => {
                                const myMobile = `${resolveMobile(this.props.student) || ""}`.trim();
                                return pageItems.map((s, idx) => {
                                    const key = `${s.mobile ?? idx}`;
                                    const isMe = myMobile && `${s.mobile || ""}`.trim() === myMobile;
                                    const isOpen = this.state.topExpanded === key;
                                    const meta = [s.class && `Class ${s.class}`, s.section, s.branch].filter(Boolean).join(" · ");
                                    return (
                                        <div key={key} className="mb-3" style={{ borderRadius: 14, border: isMe ? "1.5px solid #6366f1" : isOpen ? "1.5px solid #c7d2fe" : "1px solid #f1f2f5", background: isMe ? "linear-gradient(180deg,#eef2ff 0%,#f5f7ff 100%)" : "#fff", boxShadow: isMe ? "0 6px 18px rgba(99,102,241,0.18)" : "0 4px 12px rgba(17,24,39,0.04)", overflow: "hidden" }}>
                                            <div className="d-flex align-items-center p-3" style={{ gap: 12, cursor: "pointer" }}
                                                onClick={() => this.setState({ topExpanded: isOpen ? null : key })}>
                                                <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0 font-weight-bold"
                                                    style={{ width: 40, height: 40, borderRadius: 11, background: this.rankColor(num(s.rank)), fontSize: 15 }}>
                                                    {s.rank != null ? `#${s.rank}` : "-"}
                                                </div>
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                                        <div className="font-weight-bold text-dark text-truncate" style={{ fontSize: 14 }}>{s.name || "-"}</div>
                                                        {isMe ? <span className="flex-shrink-0" style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#6366f1", borderRadius: 20, padding: "2px 8px", letterSpacing: 0.3 }}>YOU</span> : null}
                                                    </div>
                                                    <div className="text-muted d-flex align-items-center flex-wrap" style={{ fontSize: 12, gap: 10 }}>
                                                        {meta ? <span><i className="fas fa-graduation-cap mr-1" />{meta}</span> : null}
                                                        {s.avg != null ? <span style={{ color: "#16a34a", fontWeight: 600 }}><i className="fas fa-bullseye mr-1" />{s.avg}</span> : null}
                                                        {s.total_exams != null ? <span><i className="fas fa-file-alt mr-1" />{s.total_exams} exams</span> : null}
                                                    </div>
                                                </div>
                                                <i className={`fas fa-chevron-${isOpen ? "up" : "down"} flex-shrink-0`} style={{ color: "#94a3b8", fontSize: 12 }} />
                                            </div>
                                            {isOpen ? this.renderTopStudentDetail(s) : null}
                                        </div>
                                    );
                                });
                            })()
                        ) : (
                            <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>No top students available.</div>
                        )}

                        {!topLoading && (curPage > 1 || topHasMore) ? (
                            <div className="d-flex align-items-center justify-content-between mt-2 pt-2" style={{ borderTop: "1px solid #f1f2f5" }}>
                                <button type="button" disabled={curPage <= 1} onClick={() => goPage(curPage - 1)}
                                    style={{ fontSize: 12, fontWeight: 600, color: curPage <= 1 ? "#cbd5e1" : "#6366f1", background: "transparent", border: "none", cursor: curPage <= 1 ? "default" : "pointer" }}>
                                    <i className="fas fa-chevron-left mr-1" />Prev
                                </button>
                                <span className="text-muted font-weight-bold" style={{ fontSize: 12 }}>Page {curPage}</span>
                                <button type="button" disabled={!topHasMore} onClick={() => goPage(curPage + 1)}
                                    style={{ fontSize: 12, fontWeight: 600, color: !topHasMore ? "#cbd5e1" : "#6366f1", background: "transparent", border: "none", cursor: !topHasMore ? "default" : "pointer" }}>
                                    Next<i className="fas fa-chevron-right ml-1" />
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </>
        );
    };

    // Score Trend vs Batch — PER EXAM: the student's total score vs the average
    // total score of their class+branch batchmates on each exam. Always across
    // all exams (a trend needs multiple points); the selected exam is highlighted.
    renderTrendChart = (_overallIgnored, highlightExam) => {
        const trend = this.state.scoreTrend || [];
        const categories = trend.map((r) => r.exam_name || r.label || "-");
        const hlName = highlightExam && highlightExam.exam_name;
        // Each point carries the exam's max marks so the tooltip can show "X / max".
        const scoreData = trend.map((r) => {
            const p = { y: num(r.score), outOf: num(r.total_marks) };
            if (hlName && r.exam_name === hlName) p.color = "#4f46e5";
            return p;
        });
        const batchData = trend.map((r) => (r.batch_avg != null ? { y: num(r.batch_avg), outOf: num(r.total_marks) } : null));
        const topperData = trend.map((r) => (r.topper_score != null ? { y: num(r.topper_score), outOf: num(r.total_marks), topperName: r.topper_name || "" } : null));

        const options = {
            chart: { type: "column", backgroundColor: "transparent", height: 320 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600", fontSize: "11px" }, rotation: categories.length > 4 ? -35 : 0 },
            },
            yAxis: {
                min: 0,
                title: { text: "Score", style: { color: "#94a3b8", fontSize: "11px" } },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            tooltip: {
                enabled: true, shared: false, useHTML: true,
                backgroundColor: "#fff", borderColor: "#e2e8f0", borderRadius: 10, shadow: true, padding: 0,
                formatter: function () {
                    const color = this.series.color;
                    const isTopper = this.series.name === "Topper";
                    const who = isTopper && this.point.topperName
                        ? `<div style="font-size:11px;color:#94a3b8">${this.point.topperName}</div>` : "";
                    // Show "score / max" using the exam's total marks when known.
                    const outOf = this.point && this.point.outOf ? this.point.outOf : null;
                    const valStr = outOf
                        ? `${this.y}<span style="color:#94a3b8;font-weight:600;font-size:12px"> / ${outOf}</span>`
                        : `${this.y}`;
                    return (
                        `<div style="min-width:120px;padding:8px 12px">` +
                        `<div style="font-size:11px;color:#94a3b8;margin-bottom:3px">${this.key || ""}</div>` +
                        `<div style="display:flex;align-items:center;gap:7px">` +
                        `<span style="width:9px;height:9px;border-radius:50%;background:${color};display:inline-block"></span>` +
                        `<span style="font-size:12.5px;font-weight:700;color:#334155">${this.series.name}</span></div>` +
                        who +
                        `<div style="margin-top:3px;font-size:15px;font-weight:700;color:${color}">${valStr}</div>` +
                        `</div>`
                    );
                },
            },
            legend: { align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
            plotOptions: {
                column: {
                    borderRadius: 4,
                    groupPadding: 0.12,
                    pointPadding: 0.04,
                    dataLabels: {
                        enabled: true,
                        // allowOverlap skips Highcharts' hideOverlappingLabels pass,
                        // which crashes (labelrank) when some series are hidden/null.
                        allowOverlap: true,
                        formatter: function () { return this.y != null && this.y > 0 ? this.y : null; },
                        style: { fontWeight: "700", fontSize: "10px", color: "#475569", textOutline: "none" },
                    },
                },
                spline: { dataLabels: { enabled: false }, states: { hover: { lineWidthPlus: 1 } } },
            },
            series: [
                {
                    // The student's score per exam (highlighted).
                    name: studentFirstName(this.props.student),
                    data: scoreData,
                    color: "#6366f1",
                },
                {
                    // Batch Average — a benchmark LINE across the bars, not a bar.
                    name: "Batch Average",
                    type: "spline",
                    data: batchData,
                    color: "#64748b",
                    lineWidth: 2.5,
                    dashStyle: "ShortDash",
                    marker: { enabled: true, radius: 3, symbol: "circle" },
                    dataLabels: { enabled: false },
                    // Show whenever batch data exists (points are {y, outOf} objects,
                    // so check v.y — not the object itself).
                    visible: batchData.some((v) => v != null && num(v.y) > 0),
                },
                {
                    // Topper — highest scorer on each exam.
                    name: "Topper",
                    data: topperData,
                    color: "#16a34a",
                    visible: topperData.some((v) => v != null && num(v.y) > 0),
                },
            ],
        };

        // How many batchmates the average spans (for the subtitle).
        const batchSize = trend.reduce((mx, r) => Math.max(mx, num(r.batch_size)), 0);

        return (
            <div
                className="p-3 mt-4"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Score Trend vs Batch
                </h6>
                <span className="text-muted d-block mb-3" style={{ fontSize: "11.5px" }}>
                    {studentFirstName(this.props.student)}'s total score per exam vs class &amp; branch <b style={{ color: "#94a3b8" }}>average</b> and the <b style={{ color: "#16a34a" }}>topper</b>
                    {batchSize > 0 ? ` (${batchSize} batchmates)` : ""}
                </span>
                {trend.length > 0 ? (
                    // Horizontal scroll when there are many exams: give each exam a
                    // fixed slot (~90px for its 3 grouped bars) so bars never squish;
                    // the container scrolls when total width exceeds the panel.
                    (() => {
                        const minWidth = Math.max(trend.length * 90, 320);
                        return (
                            <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                                <div style={{ minWidth }}>
                                    <HighchartsReact highcharts={Highcharts} options={options} />
                                </div>
                            </div>
                        );
                    })()
                ) : (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No score trend available.
                    </div>
                )}
            </div>
        );
    };


    // Subject-wise Performance — best/lowest score vs branch & section averages per subject.
    renderSubjectWisePerformance = (subjectPerformance = []) => {
        const options = {
            chart: {
                type: "bar",
                backgroundColor: "#f5f7fa",
                borderRadius: 10,
                spacingLeft: 20,
                height: 320,
                scrollablePlotArea: {
                    minWidth: subjectPerformance?.length > 10 ? subjectPerformance.length * 80 : null,
                    scrollPositionX: 0,
                },
            },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories: subjectPerformance.map((item) => item.subject),
                scrollbar: { enabled: true },
                labels: { style: { fontWeight: "600", color: "#000" } },
            },
            yAxis: {
                title: { text: "Score" },
                tickInterval: 5,
            },
            tooltip: { enabled: true },
            plotOptions: {
                bar: { dataLabels: { enabled: true } },
                series: { borderRadius: 6, pointPadding: 0.1, groupPadding: 0.2 },
            },
            legend: {
                layout: "vertical",
                align: "right",
                verticalAlign: "top",
                x: -40,
                y: 80,
            },
            series: [
                { name: "Best Score", data: subjectPerformance.map((i) => num(i.best_score)), color: "#1f4e79" },
                { name: "Lowest Score", data: subjectPerformance.map((i) => num(i.lowest_score)), color: "#2ecc71" },
                { name: "Branch Avg", data: subjectPerformance.map((i) => num(i.branch_avg)), color: "#007bff" },
                { name: "Section Avg", data: subjectPerformance.map((i) => num(i.section_avg)), color: "#e74c3c" },
            ],
            exporting: { enabled: true },
        };

        return (
            <div
                className="p-3 mt-4"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Subject-wise Performance
                </h6>
                {subjectPerformance.length > 0 ? (
                    <HighchartsReact highcharts={Highcharts} options={options} />
                ) : (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No subject-wise performance available.
                    </div>
                )}
            </div>
        );
    };

    // Modal listing individual exam attempts (examWiseResult) for the clicked row.
    // Attempt rate tracking — questions attempted vs. skipped in each test.
    renderAttemptChart = (rows) => {
        const categories = rows.map((r, i) => r.exam_name || r.date || `Test ${i + 1}`);
        const attempted = rows.map((r) => num(r.correct) + num(r.wrong));
        const skipped = rows.map((r) => num(r.not_answered));
        const options = {
            chart: { type: "column", backgroundColor: "transparent", height: 320 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: {
                min: 0,
                title: { text: "Questions" },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            tooltip: { shared: false, enabled: true },
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: { column: { borderRadius: 4, groupPadding: 0.15, dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } } },
            series: [
                { name: "Attempted", data: attempted, color: "#22c55e" },
                { name: "Skipped", data: skipped, color: "#ef4444" },
            ],
        };
        return (
            <div>
                <button
                    type="button"
                    className="btn btn-link p-0 mb-3"
                    style={{ fontWeight: 600, color: "#4f46e5" }}
                    onClick={() => this.setState({ showAttemptChart: false, selectedExamRow: null })}
                >
                    <i className="fas fa-angle-left mr-1" /> Back to results
                </button>
                <h6 className="font-weight-bold text-dark mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Attempt Rate Tracking — questions attempted vs. skipped in each test
                </h6>
                {rows.length > 0 ? (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                        containerProps={{ style: { width: "100%" } }}
                    />
                ) : (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No attempt data available.
                    </div>
                )}
            </div>
        );
    };

    renderExamModal = () => {
        const { showExamModal, showAttemptChart, modalTitle, modalRows, selectedExamRow } = this.state;
        // Chart only the selected exam — match by exam_paper_id / id, else the captured index.
        let chartRows = modalRows;
        if (selectedExamRow) {
            const matched = modalRows.filter((r) =>
                selectedExamRow.exam_paper_id != null
                    ? r.exam_paper_id === selectedExamRow.exam_paper_id
                    : selectedExamRow.id != null
                        ? r.id === selectedExamRow.id
                        : false
            );
            chartRows = matched.length
                ? matched
                : (modalRows[selectedExamRow.__index] ? [modalRows[selectedExamRow.__index]] : [selectedExamRow]);
        }
        return (
            <Modal show={showExamModal} onHide={this.closeExamModal} size={showAttemptChart ? undefined : "xl"}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: "18px" }}>
                        {modalTitle} — {showAttemptChart ? "Attempt Rate" : "Exam-wise Results"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalRows.length > 0 ? (
                        showAttemptChart ? (
                            this.renderAttemptChart(chartRows)
                        ) : (
                            <CardLessDataTableWithSearch
                                parentData={modalRows}
                                particlecolumns={this.modalColumns}
                                name="report"
                            />
                        )
                    ) : (
                        <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                            No exam-wise results available.
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // Snapshot card — quick at-a-glance summary (attendance, subjects, prediction, risk).
    renderSnapshot = (snapshot) => {
        const s = snapshot || {};
        const targetLabel = (s.target || "").toUpperCase();
        const rows = [
            { label: "Attendance", value: s.attendance != null ? `${s.attendance}%` : "-", color: "#16a34a" },
            { label: "Best Subject", value: this.formatValue(s.best_subject), color: "#1e293b", clickable: true },
            { label: "Weak Subject", value: this.formatValue(s.weak_subject), color: "#dc2626", clickable: true },
            {
                label: `Predicted ${targetLabel || "Score"}`,
                value:
                    s.predicted_score != null
                        ? `${s.predicted_score}${s.max_score != null ? `/${s.max_score}` : ""}`
                        : "-",
                color: "#1e293b",
            },
            {
                label: "Sim All-India Rank",
                value: s.simulated_air != null ? `~${Number(s.simulated_air).toLocaleString()}` : "-",
                color: "#1e293b",
            },
            { label: "Risk Level", value: this.formatValue(s.risk_level), color: this.riskColor(s.risk_level) },
        ];
        return (
            <div
                className="p-4 mt-4 h-100"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <h6 className="font-weight-bold text-dark mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Snapshot
                </h6>
                {snapshot ? (
                    rows.map((r, i) => (
                        <div
                            key={r.label}
                            className="d-flex align-items-center justify-content-between py-2"
                            style={{
                                borderBottom: i < rows.length - 1 ? "1px solid #f1f2f5" : "none",
                                cursor: r.clickable ? "pointer" : "default",
                            }}
                            onClick={r.clickable ? () => this.openSubjectMatrix(r.label) : undefined}
                            title={r.clickable ? "View exam-wise subject breakdown" : undefined}
                        >
                            <span className="text-muted" style={{ fontSize: "14px" }}>
                                {r.label}
                                {r.clickable ? <i className="fas fa-table ml-2" style={{ fontSize: 11, color: "#7c3aed" }} /> : null}
                            </span>
                            <span className="font-weight-bold d-inline-flex align-items-center" style={{ fontSize: "15px", color: r.color, gap: 6 }}>
                                {r.value}
                                {r.clickable ? <i className="fas fa-chevron-right" style={{ fontSize: 10, color: "#cbd5e1" }} /> : null}
                            </span>
                        </div>
                    ))
                ) : (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No snapshot available.
                    </div>
                )}
            </div>
        );
    };

    // Stat card → detailed exam-paper breakdown (from examSubjectMatrix): per exam,
    // which subjects, their score/accuracy/attempted, aggregates, best exam highlighted.
    renderSummaryModal = () => {
        const { summaryModalOpen, summaryMetric, examSubjectMatrix, summaryView, examWiseResult, overallData } = this.state;
        if (!summaryModalOpen || !summaryMetric) return null;

        // Per-exam-paper aggregates computed from its subjects.
        const exams = aggregateExams(examSubjectMatrix);

        // Rank exams by the clicked metric so the best one floats to the top with a badge.
        const field = summaryMetric.field;
        const sortVal = (e) =>
            field === "accuracy" ? e.avgAccuracy : field === "average_score" ? e.avgScore : e.totalScore;
        const sorted = [...exams].sort((a, b) => sortVal(b) - sortVal(a));
        const topId = sorted.length ? sorted[0].exam_paper_id : null;

        // Rank per exam — matched precisely by the exam's session/paper id first
        // (name matching collapses same-named exams onto one rank), then by name.
        const rankById = {};
        const rankByName = {};
        (examWiseResult || []).forEach((r) => {
            if (r.exam_rank == null) return;
            if (r.id != null) rankById[String(r.id)] = num(r.exam_rank);
            const k = String(r.exam_name || "").trim();
            if (k && rankByName[k] == null) rankByName[k] = num(r.exam_rank);
        });
        (overallData || []).forEach((r) => {
            const k = String(r.exam_name || "").trim();
            if (k && rankByName[k] == null && r.avg_rank != null) rankByName[k] = num(r.avg_rank);
        });
        const rankOf = (e) => {
            if (e.session_id != null && rankById[String(e.session_id)] != null) return rankById[String(e.session_id)];
            if (e.exam_paper_id != null && rankById[String(e.exam_paper_id)] != null) return rankById[String(e.exam_paper_id)];
            const k = String(e.exam_name || "").trim();
            return rankByName[k] != null ? rankByName[k] : null;
        };

        // The clicked metric's value per exam (bar), plus rank (line).
        const metricValueOf = (e) =>
            field === "accuracy" ? e.avgAccuracy : field === "average_score" ? e.avgScore : field === "highest_score" ? e.highestScore : e.totalScore;
        const metricSuffix = field === "accuracy" ? "%" : "";
        const metricColor = summaryMetric.noteColor || "#6366f1";

        // Graph: chronological across exams. Total Score card → combined bar + line
        // view (Total Score & Avg Score bars + Avg Accuracy line); others → single metric.
        const byDate = [...exams].sort((a, b) => parseExamDate(a.date) - parseExamDate(b.date));
        const isTotalView = field === "total_score";
        const chartOptions = {
            chart: { backgroundColor: "transparent", height: 360 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: { categories: byDate.map((e) => e.exam_name || `Exam ${e.exam_paper_id}`), lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
            yAxis: isTotalView
                ? [
                    { title: { text: "Score" }, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
                    { title: { text: "Accuracy %" }, opposite: true, max: 100, labels: { format: "{value}%", style: { color: "#64748b" } } },
                ]
                : [
                    { title: { text: summaryMetric.label }, gridLineColor: "#eef2f6", max: field === "accuracy" ? 100 : undefined, labels: { style: { color: "#64748b" } } },
                ],
            tooltip: { shared: false, enabled: true },
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: { column: { borderRadius: 5, groupPadding: 0.14, dataLabels: { enabled: true, format: isTotalView ? "{point.y}" : `{point.y}${metricSuffix}`, style: { fontWeight: "700", color: "#334155" } } }, spline: { dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } } },
            series: isTotalView
                ? [
                    { name: "Total Score", type: "column", data: byDate.map((e) => e.totalScore), color: "#6366f1" },
                    { name: "Avg Score", type: "column", data: byDate.map((e) => e.avgScore), color: "#a855f7" },
                    { name: "Avg Accuracy %", type: "spline", yAxis: 1, data: byDate.map((e) => e.avgAccuracy), color: "#16a34a", lineWidth: 3, marker: { radius: 4 }, tooltip: { valueSuffix: "%" }, dataLabels: { enabled: true, format: "{point.y}%", style: { fontWeight: "700", color: "#16a34a" } } },
                ]
                : [
                    { name: summaryMetric.label, type: "column", data: byDate.map((e) => metricValueOf(e)), color: metricColor, tooltip: { valueSuffix: metricSuffix } },
                ],
        };

        const chip = (icon, label, value, color) => (
            <span className="d-inline-flex align-items-center" style={{ gap: 6, padding: "5px 12px", borderRadius: 9, background: "#f4f3ff", border: "1px solid #e7e3fb", color: color || "#5b4fd6", fontSize: 12.5, fontWeight: 600 }}>
                <i className={`fas ${icon}`} style={{ fontSize: 11 }} /> {label}: <strong>{value}</strong>
            </span>
        );

        const seg = (view, icon, label) => (
            <button type="button" className={summaryView === view ? "active" : ""} onClick={() => this.setState({ summaryView: view })}>
                <i className={`fas ${icon}`} />{label}
            </button>
        );

        // Single pivot table: rows = subjects (union), columns = exams (chronological).
        const subjectRows = [];
        const subjSeen = new Set();
        byDate.forEach((e) => (e.subjects || []).forEach((s) => {
            const name = s.subject || `#${s.subject_id}`;
            if (!subjSeen.has(name)) { subjSeen.add(name); subjectRows.push(name); }
        }));
        const cellFor = (e, name) => (e.subjects || []).find((s) => (s.subject || `#${s.subject_id}`) === name) || null;

        return (
            <Modal show onHide={() => this.setState({ summaryModalOpen: false })} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: "17px" }}>
                        <i className={`fas ${summaryMetric.icon} mr-2`} style={{ color: summaryMetric.noteColor }} />
                        {summaryMetric.label} — Exam-wise Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-end mb-3">
                        <div className="sp-segment">
                            {seg("graph", "fa-chart-column", "Graph")}
                            {seg("table", "fa-table", "Table")}
                        </div>
                    </div>
                    {exams.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No exam-wise subject data available.</div>
                    ) : summaryView === "graph" ? (
                        <div style={{ borderRadius: 14, border: "1px solid #f1f2f5", padding: 16 }}>
                            <div className="font-weight-bold text-dark mb-2" style={{ fontSize: 14 }}>
                                {summaryMetric.label} — exam-wise
                            </div>
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </div>
                    ) : (
                        <>
                            {/* Cell format legend */}
                            <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 14, fontSize: 11.5, color: "#64748b" }}>
                                <span className="font-weight-bold text-muted">Each cell:</span>
                                <span><strong style={{ color: "#1e293b" }}>00</strong> Score</span>
                                <span><strong style={{ color: "#16a34a" }}>00%</strong> Accuracy</span>
                                <span><strong style={{ color: "#64748b" }}>00Q</strong> Questions attempted</span>
                            </div>
                            <div className="table-responsive" style={{ borderRadius: 12, border: "1px solid #f1f2f5" }}>
                            <table className="table mb-0" style={{ fontSize: 13 }}>
                                <thead>
                                    {/* Column headers = exam names */}
                                    <tr style={{ background: "#faf9ff" }}>
                                        <th style={{ fontSize: 11, textTransform: "uppercase", color: "#94a3b8", fontWeight: 700, padding: "10px 12px", position: "sticky", left: 0, background: "#faf9ff", minWidth: 120 }}>Subject</th>
                                        {byDate.map((e) => {
                                            const isTop = e.exam_paper_id === topId;
                                            return (
                                                <th key={e.exam_paper_id} className="text-center" style={{ fontSize: 11, fontWeight: 700, color: isTop ? "#16a34a" : "#475569", padding: "10px 12px", minWidth: 120 }}>
                                                    <div className="text-truncate" style={{ maxWidth: 160 }} title={e.exam_name}>
                                                        {e.exam_name || `Exam ${e.exam_paper_id}`}
                                                        {isTop ? <i className="fas fa-star ml-1" style={{ fontSize: 9, color: "#16a34a" }} /> : null}
                                                    </div>
                                                    {e.date ? <div className="text-muted" style={{ fontSize: 10, fontWeight: 400 }}>{e.date}</div> : null}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Rows = subjects; cells = score + accuracy/attempted per exam */}
                                    {subjectRows.map((name) => (
                                        <tr key={name} style={{ borderTop: "1px solid #f4f4f7" }}>
                                            <td style={{ fontWeight: 700, color: "#1e293b", padding: "10px 12px", position: "sticky", left: 0, background: "#fff" }}>{name}</td>
                                            {byDate.map((e) => {
                                                const s = cellFor(e, name);
                                                if (!s) return <td key={e.exam_paper_id} className="text-center text-muted" style={{ padding: "10px 12px" }}>-</td>;
                                                const acc = num(s.accuracy);
                                                return (
                                                    <td key={e.exam_paper_id} className="text-center" style={{ padding: "10px 12px" }}>
                                                        <div className="font-weight-bold" style={{ color: "#1e293b" }}>{num(s.score)}</div>
                                                        <div style={{ fontSize: 11, color: heatColor(acc) }}>
                                                            {acc}% · {num(s.attempted)}Q
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {/* Totals row */}
                                    <tr style={{ borderTop: "2px solid #eef2f6", background: "#faf9ff" }}>
                                        <td style={{ fontWeight: 700, color: "#4f46e5", padding: "10px 12px", position: "sticky", left: 0, background: "#faf9ff" }}>Total Score</td>
                                        {byDate.map((e) => (
                                            <td key={e.exam_paper_id} className="text-center font-weight-bold" style={{ color: "#4f46e5", padding: "10px 12px" }}>{e.totalScore}</td>
                                        ))}
                                    </tr>
                                    <tr style={{ background: "#faf9ff" }}>
                                        <td style={{ fontWeight: 600, color: "#16a34a", padding: "8px 12px", position: "sticky", left: 0, background: "#faf9ff" }}>Avg Accuracy</td>
                                        {byDate.map((e) => (
                                            <td key={e.exam_paper_id} className="text-center font-weight-bold" style={{ color: "#16a34a", padding: "8px 12px" }}>{e.avgAccuracy}%</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // Open the Best/Weak subject modal defaulting its exam selection to the latest exam.
    openSubjectMatrix = (title) => {
        const latest = pickLatestExam(this.state.examSubjectMatrix);
        this.setState({
            subjectMatrixOpen: true,
            subjectMatrixTitle: title,
            subjectMatrixView: "graph",
            selectedMatrixPaper: latest ? { value: latest.exam_paper_id, label: latest.exam_name } : null,
        });
    };

    // Best/Weak Subject → modal with an exam-paper dropdown + stylish subject table.
    renderSubjectMatrixModal = () => {
        const { subjectMatrixOpen, subjectMatrixTitle, examSubjectMatrix, selectedMatrixPaper } = this.state;
        if (!subjectMatrixOpen) return null;

        const paperOptions = examSubjectMatrix.map((m) => ({ value: m.exam_paper_id, label: m.exam_name }));
        const latestExam = pickLatestExam(examSubjectMatrix);
        const selected =
            examSubjectMatrix.find((m) => String(m.exam_paper_id) === String(selectedMatrixPaper?.value)) ||
            latestExam;
        const subjects = selected?.subjects || [];
        const isBest = `${subjectMatrixTitle}`.toLowerCase().includes("best");

        // Highlight the best (highest) or weak (lowest) subject by accuracy.
        let highlightId = null;
        if (subjects.length) {
            const sorted = [...subjects].sort((a, b) => num(b.accuracy) - num(a.accuracy));
            highlightId = (isBest ? sorted[0] : sorted[sorted.length - 1])?.subject_id;
        }

        const th = { fontSize: 11, textTransform: "uppercase", color: "#94a3b8", fontWeight: 700, padding: "10px 12px", letterSpacing: "0.4px" };
        const view = this.state.subjectMatrixView;

        // Graph: per-subject Accuracy % (colored by strength) + Score, with best/weak highlighted.
        const subjChart = {
            chart: { backgroundColor: "transparent", height: 340 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: { categories: subjects.map((s) => s.subject || "-"), lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
            yAxis: [
                { title: { text: "Accuracy %" }, max: 100, gridLineColor: "#eef2f6", labels: { format: "{value}%", style: { color: "#64748b" } } },
                { title: { text: "Score" }, opposite: true, gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } } },
            ],
            tooltip: { enabled: true, shared: false },
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: { column: { borderRadius: 5, groupPadding: 0.14, dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } } },
            series: [
                {
                    name: "Accuracy %",
                    type: "column",
                    data: subjects.map((s) => ({
                        y: num(s.accuracy),
                        color: s.subject_id === highlightId ? (isBest ? "#16a34a" : "#dc2626") : heatColor(num(s.accuracy)),
                    })),
                    dataLabels: { format: "{point.y}%" },
                },
                { name: "Score", type: "spline", yAxis: 1, data: subjects.map((s) => num(s.score)), color: "#6366f1", lineWidth: 3, marker: { radius: 4 } },
            ],
        };

        const seg = (v, icon, label) => (
            <button type="button" className={view === v ? "active" : ""} onClick={() => this.setState({ subjectMatrixView: v })}>
                <i className={`fas ${icon}`} />{label}
            </button>
        );

        // Pivot table: rows = subjects (union), columns = exams (chronological).
        const pivotExams = [...examSubjectMatrix].sort((a, b) => parseExamDate(a.date) - parseExamDate(b.date));
        const subjRows = [];
        const subjSeen = new Set();
        pivotExams.forEach((e) => (e.subjects || []).forEach((s) => {
            const name = s.subject || `#${s.subject_id}`;
            if (!subjSeen.has(name)) { subjSeen.add(name); subjRows.push(name); }
        }));
        const cellForSub = (e, name) => (e.subjects || []).find((s) => (s.subject || `#${s.subject_id}`) === name) || null;
        const highlightName = subjects.find((s) => s.subject_id === highlightId)?.subject || null;

        return (
            <Modal show onHide={() => this.setState({ subjectMatrixOpen: false, selectedMatrixPaper: null })} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: "17px" }}>
                        <i className={`fas ${isBest ? "fa-star" : "fa-triangle-exclamation"} mr-2`} style={{ color: isBest ? "#16a34a" : "#dc2626" }} />
                        {subjectMatrixTitle} — Exam-wise Subject Breakdown
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 12 }}>
                        <span className="font-weight-bold text-muted" style={{ fontSize: 13 }}>Exam Paper</span>
                        <div style={{ minWidth: 230 }} className="batch-filter-select">
                            <SelectDropDown
                                stateData={selectedMatrixPaper || (latestExam ? { value: latestExam.exam_paper_id, label: latestExam.exam_name } : null)}
                                handleChange={(n, v, opt) => this.setState({ selectedMatrixPaper: opt })}
                                name="selectedMatrixPaper"
                                options={paperOptions}
                                placeholderName="Exam Paper"
                                dropdownIndicator={{ DropdownIndicator }}
                            />
                        </div>
                        {selected?.date ? <span className="text-muted" style={{ fontSize: 12 }}><i className="far fa-calendar mr-1" />{selected.date}</span> : null}
                        <div className="sp-segment ml-auto">
                            {seg("graph", "fa-chart-column", "Graph")}
                            {seg("table", "fa-table", "Table")}
                        </div>
                    </div>

                    {subjects.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No subject data for this exam.</div>
                    ) : view === "graph" ? (
                        <div style={{ borderRadius: 12, border: "1px solid #f1f2f5", padding: 12 }}>
                            <HighchartsReact highcharts={Highcharts} options={subjChart} />
                        </div>
                    ) : (
                        <>
                            {/* Cell format legend */}
                            <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 14, fontSize: 11.5, color: "#64748b" }}>
                                <span className="font-weight-bold text-muted">Each cell:</span>
                                <span><strong style={{ color: "#1e293b" }}>00</strong> Score</span>
                                <span><strong style={{ color: "#16a34a" }}>00%</strong> Accuracy</span>
                                <span><strong style={{ color: "#64748b" }}>00Q</strong> Questions attempted</span>
                            </div>
                            <div className="table-responsive" style={{ borderRadius: 12, border: "1px solid #f1f2f5" }}>
                            <table className="table mb-0" style={{ fontSize: 13 }}>
                                <thead>
                                    {/* Column headers = exam names */}
                                    <tr style={{ background: "#faf9ff" }}>
                                        <th style={{ ...th, position: "sticky", left: 0, background: "#faf9ff", minWidth: 120 }}>Subject</th>
                                        {pivotExams.map((e) => (
                                            <th key={e.exam_paper_id} style={{ ...th, textAlign: "center", minWidth: 120 }}>
                                                <div className="text-truncate" style={{ maxWidth: 160 }} title={e.exam_name}>{e.exam_name || `Exam ${e.exam_paper_id}`}</div>
                                                {e.date ? <div className="text-muted" style={{ fontSize: 10, fontWeight: 400, textTransform: "none" }}>{e.date}</div> : null}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Rows = subjects; the best/weak subject row is highlighted */}
                                    {subjRows.map((name) => {
                                        const hl = highlightName != null && name === highlightName;
                                        return (
                                            <tr key={name} style={{ background: hl ? (isBest ? "#f0fdf4" : "#fef2f2") : "#fff", borderTop: "1px solid #f4f4f7" }}>
                                                <td style={{ padding: "12px", fontWeight: 700, color: "#1e293b", position: "sticky", left: 0, background: hl ? (isBest ? "#f0fdf4" : "#fef2f2") : "#fff" }}>
                                                    {name}
                                                    {hl ? (
                                                        <span className="ml-2" style={{ fontSize: 10, fontWeight: 700, color: isBest ? "#16a34a" : "#dc2626", background: isBest ? "#dcfce7" : "#fee2e2", borderRadius: 6, padding: "2px 7px" }}>
                                                            {isBest ? "BEST" : "WEAK"}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                {pivotExams.map((e) => {
                                                    const s = cellForSub(e, name);
                                                    if (!s) return <td key={e.exam_paper_id} className="text-center text-muted" style={{ padding: "12px" }}>-</td>;
                                                    const acc = num(s.accuracy);
                                                    return (
                                                        <td key={e.exam_paper_id} className="text-center" style={{ padding: "12px" }}>
                                                            <div className="font-weight-bold" style={{ color: "#1e293b" }}>{num(s.score)}</div>
                                                            <div style={{ fontSize: 11, color: heatColor(acc) }}>{acc}% · {num(s.attempted)}Q</div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // Chapter-comparison table columns (flattened across sections).
    // Student-only chapter table: his subjects → his chapters → his accuracy.
    // No branch/section. "—" where a chapter hasn't been attempted.
    batchChapterColumns = [
        { dataField: "subject", text: "Subject", sort: true, formatter: (cell) => this.formatValue(cell) },
        { dataField: "chapter", text: "Chapter", sort: true, formatter: (cell) => this.formatValue(cell) },
        {
            dataField: "accuracy",
            text: "Accuracy",
            sort: true,
            formatter: (cell) => (cell != null ? `${num(cell)}%` : "—"),
        },
    ];

    // Clicking a heatmap cell opens a detail modal for that section + subject + chapter.
    // The 3rd arg is a CHAPTER name (heatmap columns are chapters), so topics are
    // filtered by chapter — matching on trimmed names to survive padded values.
    openBatchDetail = (sec, subject, chapter, value) => {
        const label = sec.section_name || `Section ${sec.section_id}`;
        const chapKey = chapter != null ? String(chapter).trim() : "";
        const chapters = (sec.chapters || []).filter(
            (c) => c.subject === subject && (!chapKey || String(c.chapter || "").trim() === chapKey)
        );
        const topics = (sec.topics || []).filter(
            (t) => t.subject === subject && (!chapKey || String(t.chapter || "").trim() === chapKey)
        );
        this.setState({
            batchDetail: {
                sectionLabel: label,
                branchName: sec.branch_name,
                subject,
                chapter,
                value,
                studentCount: sec.student_count,
                chapters,
                topics,
            },
        });
    };

    closeBatchDetail = () => this.setState({ batchDetail: null });

    renderBatchDetailModal = () => {
        const d = this.state.batchDetail;
        if (!d) return null;
        const chipStyle = {
            display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px",
            borderRadius: 9, background: "#f4f3ff", border: "1px solid #e7e3fb",
            color: "#5b4fd6", fontSize: 12.5, fontWeight: 600,
        };
        const accCell = (v) => (
            <span className="font-weight-bold" style={{ color: v == null ? "#94a3b8" : heatColor(num(v)) }}>
                {v == null ? "-" : `${num(v)}%`}
            </span>
        );
        return (
            <Modal show onHide={this.closeBatchDetail} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: "17px" }}>
                        {d.sectionLabel}{d.branchName ? ` — ${d.branchName}` : ""} · {d.subject}{d.chapter ? ` · ${d.chapter}` : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
                        <span style={chipStyle}><i className="fas fa-bullseye" style={{ fontSize: 11 }} /> Accuracy {d.value == null ? "—" : `${d.value}%`}</span>
                        {d.studentCount != null && (
                            <span style={chipStyle}><i className="fas fa-user-graduate" style={{ fontSize: 11 }} /> {num(d.studentCount)} Students</span>
                        )}
                        <span style={chipStyle}><i className="fas fa-book" style={{ fontSize: 11 }} /> {d.chapters.length} Chapters</span>
                        <span style={chipStyle}><i className="fas fa-list" style={{ fontSize: 11 }} /> {d.topics.length} Topics</span>
                    </div>

                    <h6 className="font-weight-bold text-dark mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <i className="fas fa-book mr-2" style={{ color: "#7c3aed" }} />Chapters
                    </h6>
                    {d.chapters.length > 0 ? (
                        <div className="table-responsive mb-4">
                            <table className="table table-borderless mb-0" style={{ fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>Chapter</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase" }}>Avg Accuracy</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.chapters.map((c, i) => (
                                        <tr key={c.chapter_id ?? `${c.chapter}-${i}`} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td className="text-dark font-weight-bold">{this.formatValue(c.chapter)}</td>
                                            <td className="text-right">{accCell(c.avg_accuracy)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted mb-4" style={{ fontSize: 13 }}>No chapter data.</div>
                    )}

                    <h6 className="font-weight-bold text-dark mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <i className="fas fa-list mr-2" style={{ color: "#7c3aed" }} />Topics
                    </h6>
                    {d.topics.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-borderless mb-0" style={{ fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>Chapter</th>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>Topic</th>
                                        <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase" }}>Avg Accuracy</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.topics.map((t, i) => (
                                        <tr key={t.topic_id ?? `${t.topic}-${i}`} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td className="text-muted">{this.formatValue(t.chapter)}</td>
                                            <td className="text-dark font-weight-bold">{this.formatValue(t.topic)}</td>
                                            <td className="text-right">{accCell(t.avg_accuracy)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-muted" style={{ fontSize: 13 }}>No topic data.</div>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // Build the student's full chapter list, subject-wise: EVERY chapter offered in
    // his subjects (master list from the batch syllabus, scoped to his stream's
    // subjects), each tagged with HIS own accuracy (null where not attempted).
    // Returns rows { subject, subject_id, chapter, accuracy } sorted subject→chapter.
    buildStudentChapterRows = (scopedSections, isAllowedSubject) => {
        const norm = (s) => `${s == null ? "" : s}`.trim().toLowerCase();

        // His own attempted-chapter accuracies, keyed by subject+chapter.
        const youChapters = this.state.chapterPerformance || [];
        const accByKey = {};
        youChapters.forEach((c) => {
            const key = `${norm(c.subject)}::${norm(c.chapter)}`;
            if (c.accuracy_percentage != null && `${c.accuracy_percentage}` !== "") accByKey[key] = num(c.accuracy_percentage);
        });

        // Master chapter list: prefer the FULL syllabus (all chapters of his subjects,
        // attempted or not). Fall back to batch-derived chapters when the syllabus
        // isn't available (e.g. state exams / syllabus tables unreachable).
        const syllabus = this.state.syllabusChapters || [];
        const seen = new Set();
        const rows = [];
        const pushRow = (subject, subject_id, chapter, topic_count) => {
            const s = subject != null ? String(subject).trim() : "";
            const c = chapter != null ? String(chapter).trim() : "";
            if (!s || !c) return;
            const key = `${norm(s)}::${norm(c)}`;
            if (seen.has(key)) return;
            seen.add(key);
            const acc = accByKey[key];
            rows.push({ subject: s, subject_id, chapter: c, topic_count: topic_count || 0, accuracy: acc != null ? acc : null, attempted: acc != null });
        };

        if (syllabus.length > 0) {
            syllabus.forEach((ch) => pushRow(ch.subject, ch.subject_id, ch.chapter, ch.topic_count));
        } else {
            // Fallback: distinct chapters from the batch, scoped to the stream's subjects.
            scopedSections.forEach((sec) =>
                (sec.chapters || []).forEach((ch) => {
                    if (!isAllowedSubject(ch.subject_id)) return;
                    pushRow(ch.subject, ch.subject_id, ch.chapter, 0);
                })
            );
        }

        // Sort subject-wise, then chapter A→Z.
        rows.sort((a, b) => (a.subject === b.subject ? a.chapter.localeCompare(b.chapter) : a.subject.localeCompare(b.subject)));
        return rows;
    };

    // Chapter tile click → collect this chapter's topics (student's own topic-wise
    // accuracy + attempted counts) and open the popup.
    openChapterTopics = (subject, chapter) => {
        const norm = (s) => `${s == null ? "" : s}`.trim().toLowerCase();
        const topics = (this.state.topicPerformance || [])
            .filter((t) => norm(t.subject) === norm(subject) && norm(t.chapter) === norm(chapter))
            .map((t) => ({
                topic: t.topic || "-",
                attempted: t.attempted != null ? num(t.attempted) : 0,
                accuracy: t.accuracy_percentage != null ? num(t.accuracy_percentage) : null,
            }))
            .sort((a, b) => (a.accuracy == null) - (b.accuracy == null) || num(a.accuracy) - num(b.accuracy));
        this.setState({ chapterTopicModal: { subject, chapter, topics } });
    };

    closeChapterTopics = () => this.setState({ chapterTopicModal: null });

    // Popup: topic-wise accuracy for the clicked chapter.
    renderChapterTopicModal = () => {
        const m = this.state.chapterTopicModal;
        if (!m) return null;
        return (
            <Modal show onHide={this.closeChapterTopics} centered size="md">
                <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <div>
                        <div className="text-muted" style={{ fontSize: 11.5, fontWeight: 600 }}>{m.subject}</div>
                        <Modal.Title className="h6 font-weight-bold mb-0">{m.chapter}</Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {m.topics.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                            <i className="fas fa-list-ul d-block mb-2" style={{ fontSize: 22, color: "#cbd5e1" }} />
                            No topic-level data for this chapter yet.
                        </div>
                    ) : (
                        <table className="sp-doubts-table" style={{ minWidth: "100%" }}>
                            <thead>
                                <tr>
                                    <th>Topic</th>
                                    <th className="text-center" style={{ width: 90 }}>Attempted</th>
                                    <th className="text-center" style={{ width: 100 }}>Accuracy</th>
                                </tr>
                            </thead>
                            <tbody>
                                {m.topics.map((t, i) => (
                                    <tr key={i} style={{ cursor: "default" }}>
                                        <td style={{ fontWeight: 600, color: "#334155" }}>{t.topic}</td>
                                        <td className="text-center text-muted">{t.attempted || "—"}</td>
                                        <td className="text-center">
                                            {t.accuracy == null ? (
                                                <span className="text-muted">—</span>
                                            ) : (
                                                <span style={{ fontWeight: 700, color: heatColor(t.accuracy) === "#f8fafc" ? "#94a3b8" : "#0f172a" }}>
                                                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: heatColor(t.accuracy), marginRight: 6 }} />
                                                    {t.accuracy}%
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // Chapter-wise accuracy heatmap — THIS STUDENT ONLY, SUBJECT-WISE.
    // One block per subject; a wrapped grid of chapter cells (ALL chapters of his
    // subjects, attempted or not — "—" where not attempted), colored by his accuracy.
    renderBatchHeatmap = (scopedSections, selectedSubject = "all", isAllowedSubject) => {
        const rows = this.buildStudentChapterRows(scopedSections, isAllowedSubject || (() => true));
        const filtered = selectedSubject && selectedSubject !== "all"
            ? rows.filter((r) => r.subject === selectedSubject)
            : rows;

        if (filtered.length === 0) {
            return (
                <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>
                    <i className="fas fa-book-open d-block mb-2" style={{ fontSize: 26, color: "#cbd5e1" }} />
                    No chapters for {selectedSubject && selectedSubject !== "all" ? "this subject" : "this student"} yet.
                </div>
            );
        }

        // Group chapters by subject.
        const bySubject = [];
        const idx = {};
        filtered.forEach((r) => {
            if (idx[r.subject] == null) { idx[r.subject] = bySubject.length; bySubject.push({ subject: r.subject, chapters: [] }); }
            bySubject[idx[r.subject]].chapters.push(r);
        });

        return (
            <div>
                {/* Color-code legend */}
                <div className="d-flex align-items-center justify-content-center flex-wrap mb-3" style={{ gap: 8, fontSize: 11.5, color: "#64748b" }}>
                    <span>Weak</span>
                    {["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#22c55e"].map((c) => (
                        <span key={c} style={{ width: 24, height: 12, borderRadius: 3, background: c, display: "inline-block" }} />
                    ))}
                    <span>Strong</span>
                    <span className="ml-2" style={{ color: "#94a3b8" }}>· “—” = not attempted</span>
                </div>

                {bySubject.map((grp) => (
                    <div key={grp.subject} className="mb-3">
                        <div className="font-weight-bold text-dark mb-2" style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif" }}>
                            {grp.subject}
                            <span className="text-muted font-weight-normal ml-2" style={{ fontSize: 11 }}>{grp.chapters.length} chapters</span>
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                            {grp.chapters.map((c) => {
                                const v = c.accuracy;
                                return (
                                    <div
                                        key={c.chapter}
                                        className="sp-cx-tile"
                                        title={`${c.subject} · ${c.chapter}: ${v == null ? "not attempted" : v + "%"} — click for topics`}
                                        onClick={() => this.openChapterTopics(c.subject, c.chapter)}
                                        style={{
                                            width: 108, minHeight: 58, borderRadius: 10, padding: "8px 8px",
                                            background: v == null ? "#f8fafc" : heatColor(v),
                                            border: v == null ? "1px dashed #e2e8f0" : "1px solid rgba(0,0,0,0.06)",
                                            color: v == null ? "#94a3b8" : "#fff",
                                            display: "flex", flexDirection: "column", justifyContent: "space-between",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <span style={{ fontSize: 10, fontWeight: 600, lineHeight: 1.2 }}>{trunc(c.chapter, 34)}</span>
                                        <span style={{ fontSize: 13, fontWeight: 800, textAlign: "right" }}>{v == null ? "—" : `${v}%`}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Batch-wise average score comparison — grouped columns (subjects × sections) + chapters table.
    // Loader shown in the batch section's own card while getBatchComparison runs
    // (it fetches separately from the main report and can be slow, sometimes with a
    // second fallback call) — so the user sees a spinner, not stale/empty data.
    renderBatchLoading = () => (
        <div className="sp-af-card p-4 mt-2" style={{ borderRadius: 16 }}>
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
                <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                    style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)" }}>
                    <i className="fas fa-layer-group" style={{ fontSize: 17 }} />
                </div>
                <div>
                    <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}>
                        Batch-wise Average Score Comparison
                    </h6>
                    <span className="text-muted" style={{ fontSize: 12.5 }}>Loading batch data…</span>
                </div>
            </div>
            <div className="text-center text-muted py-5">
                <i className="fas fa-circle-notch fa-spin" style={{ fontSize: 26, color: "#6366f1" }} />
                <div className="mt-2" style={{ fontSize: 13 }}>Comparing against your class &amp; stream…</div>
            </div>
        </div>
    );

    renderBatchComparison = (sections) => {
        // Scope the comparison to the student's stream (MPC or BIPC) — strictly by SUBJECT.
        // We do NOT classify whole sections by stream (a section can carry stray cross-stream
        // questions); instead we keep only the stream's subjects and drop sections that have none.
        const stream = resolveStudentStream(this.props.student, this.state.subjectPerformance);
        const allowedIds = stream ? STREAM_SUBJECT_IDS[stream] : null;
        // Only the student's stream sections should display: a section qualifies only if it
        // carries the stream's DEFINING subject (MPC => Maths; BIPC => Botany/Zoology).
        const definingIds = stream ? STREAM_DEFINING_IDS[stream] : null;
        const sectionInStream = (sec) =>
            !definingIds || (sec.subjects || []).some((s) => definingIds.has(num(s.subject_id)));
        // Multiple branches exist under the same institution_id — compare across all of them.
        const filteredSections = definingIds ? sections.filter(sectionInStream) : sections;
        // Keep the stream filter STRICT: a JEE (MPC) student must NEVER see NEET
        // (BIPC) sections/subjects, even if the stream filter leaves nothing (that
        // just means no batch data for the current filter → show empty, not all).
        const streamSections = filteredSections;
        // The subject allowlist ALWAYS applies when we know the stream, so stray
        // cross-stream subjects (e.g. Botany/Zoology bars for a JEE student) are
        // dropped from every section that is shown.
        const effectiveAllowed = allowedIds || null;
        const isAllowedSubject = (sid) => !effectiveAllowed || effectiveAllowed.has(num(sid));

        // Branch filter options derived from the stream-scoped data (distinct branches).
        const branchOptions = [];
        const seenBranch = new Set();
        streamSections.forEach((sec) => {
            const id = sec.branch_id != null ? String(sec.branch_id) : sec.branch_name;
            if (id != null && !seenBranch.has(id)) {
                seenBranch.add(id);
                branchOptions.push({ value: id, label: sec.branch_name || `Branch ${id}` });
            }
        });

        // Default scope = the student's OWN branch/section (not all sections). The dropdowns
        // still let the user widen the comparison; "not touched" (null) uses the own-batch default.
        const stu = this.props.student || {};
        const stuBranchKey = stu.branch_id != null ? String(stu.branch_id) : (stu.branch_name ? String(stu.branch_name) : null);

        // Branch is not a dropdown here any more — it comes from the header (NavbarOne).
        // When the header names branches, they win; when it selects "All", keep the
        // previous default of comparing against the student's OWN branch.
        const headerBranchIds = getEffectiveBranchIds();
        const headerScoped = headerBranchIds.length
            ? branchOptions.filter((b) => headerBranchIds.includes(String(b.value)))
            : [];
        const ownBranch = stuBranchKey ? branchOptions.filter((b) => String(b.value) === stuBranchKey) : [];
        const branchSel = headerScoped.length
            ? headerScoped
            : (ownBranch.length ? ownBranch : branchOptions);
        const branchSelValues = new Set(branchSel.map((b) => b.value));
        const branchScoped = streamSections.filter((sec) =>
            branchSelValues.has(String(sec.branch_id != null ? sec.branch_id : sec.branch_name))
        );

        // Section filter options derived from the branch-scoped data (distinct sections).
        const sectionOptions = [];
        const seenSection = new Set();
        branchScoped.forEach((sec) => {
            const id = sec.section_id != null ? String(sec.section_id) : sec.section_name;
            if (id != null && !seenSection.has(id)) {
                seenSection.add(id);
                sectionOptions.push({ value: id, label: sec.section_name || `Section ${id}` });
            }
        });

        // null selection = "not touched" → default to ALL sections selected.
        const defaultSectionSel = sectionOptions;
        const sectionSel = this.state.batchSectionSel === null ? defaultSectionSel : this.state.batchSectionSel;
        const sectionSelValues = new Set(sectionSel.map((s) => s.value));
        const scopedSections = branchScoped.filter((sec) =>
            sectionSelValues.has(String(sec.section_id != null ? sec.section_id : sec.section_name))
        );

        // Union of the stream's subject names across scoped sections, in first-seen order.
        const subjectNames = [];
        scopedSections.forEach((sec) =>
            (sec.subjects || []).forEach((s) => {
                if (s.subject && isAllowedSubject(s.subject_id) && !subjectNames.includes(s.subject)) {
                    subjectNames.push(s.subject);
                }
            })
        );

        const series = scopedSections
            .map((sec, i) => {
                const byName = {};
                (sec.subjects || []).forEach((s) => {
                    if (isAllowedSubject(s.subject_id)) byName[s.subject] = num(s.avg_accuracy);
                });
                const secLabel = sec.section_name || `Section ${sec.section_id}`;
                return {
                    // Explicit type so a section can never inherit the student spline's type
                    // when Highcharts reconciles series by index after a check/uncheck.
                    type: "column",
                    name: sec.branch_name ? `${secLabel} — ${sec.branch_name}` : secLabel,
                    data: subjectNames.map((n) => (byName[n] != null ? byName[n] : null)),
                    color: BATCH_COLORS[i % BATCH_COLORS.length],
                };
            })
            // Drop sections with no data for the stream's subjects.
            .filter((s) => s.data.some((v) => v != null));

        // Overlay the student's own per-subject accuracy as a highlighted line, for direct comparison.
        const studentAccBySubject = {};
        (this.state.subjectPerformance || []).forEach((sp) => {
            if (sp.subject != null) studentAccBySubject[sp.subject] = num(sp.accuracy);
        });
        const studentData = subjectNames.map((n) =>
            studentAccBySubject[n] != null ? studentAccBySubject[n] : null
        );
        if (studentData.some((v) => v != null)) {
            // Unshift so the student shows first (top) in the legend; zIndex keeps it on top.
            series.unshift({
                name: studentFirstName(this.props.student),
                type: "spline",
                data: studentData,
                color: "#111827",
                lineWidth: 3,
                zIndex: 5,
                marker: { enabled: true, radius: 5, symbol: "circle", fillColor: "#111827" },
            });
        }

        const options = {
            chart: { type: "column", backgroundColor: "transparent", height: 340 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories: subjectNames,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: {
                min: 0,
                max: 100,
                title: { text: "Avg Accuracy (%)" },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            tooltip: { shared: false, valueSuffix: "%", enabled: true },
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: { column: { borderRadius: 4, groupPadding: 0.12, pointPadding: 0.04, dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } }, spline: { dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } } },
            series,
        };

        // ---- THIS STUDENT'S chapters, subject-wise (ALL chapters of his subjects,
        // attempted or not) ---- The master chapter list comes from the batch syllabus
        // (every chapter offered in his class+stream), scoped to HIS subjects; the
        // accuracy is his own (null where he hasn't attempted). No branch/section.
        const studentChapterRows = this.buildStudentChapterRows(scopedSections, isAllowedSubject);
        const chapterRows = studentChapterRows; // for any downstream refs

        // Subject filter (drives both the heatmap columns and the table rows).
        const subjectFilterOptions = [{ value: "all", label: "All Subjects" }].concat(
            subjectNames.map((n) => ({ value: n, label: n }))
        );
        const selectedSubject = this.state.batchSubjectSel?.value || "all";
        const tableRows = selectedSubject === "all" ? studentChapterRows : studentChapterRows.filter((r) => r.subject === selectedSubject);
        const batchView = this.state.batchView;

        // Chip counts reflect the current branch/section selection (scopedSections); the chips
        // themselves stay visible whenever the batch has data (gated on streamSections below).
        const totalStudents = scopedSections.reduce((sum, sec) => sum + num(sec.student_count), 0);
        const branchNames = Array.from(new Set(scopedSections.map((sec) => `${sec.branch_name ?? ""}`.trim()).filter(Boolean)));
        const sectionNames = Array.from(new Set(scopedSections.map((sec) => `${sec.section_name ?? ""}`.trim()).filter(Boolean)));
        const branchCount = new Set(scopedSections.map((sec) => sec.branch_id ?? sec.branch_name)).size;
        const sectionCount = scopedSections.length;
        // Show the actual name when a single branch/section is in scope; fall back to the count otherwise.
        const branchLabel = branchNames.length === 1 ? branchNames[0] : `${branchCount} Branch${branchCount > 1 ? "es" : ""}`;
        const sectionLabel = sectionNames.length === 1 ? sectionNames[0] : `${sectionCount} Section${sectionCount > 1 ? "s" : ""}`;
        const chipStyle = {
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 12px",
            borderRadius: 9,
            background: "#f4f3ff",
            border: "1px solid #e7e3fb",
            color: "#5b4fd6",
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
        };

        return (
            <div
                className="mt-4"
                style={{
                    borderRadius: "16px",
                    border: "1px solid #ececf6",
                    background: "#ffffff",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                }}
            >
                <div
                    className="d-flex align-items-center justify-content-between flex-wrap"
                    style={{ gap: 12, padding: "16px 20px", borderBottom: "1px solid #f1f2f5" }}
                >
                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                        <div
                            className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 12,
                                background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)",
                                boxShadow: "0 6px 14px rgba(99,102,241,0.3)",
                            }}
                        >
                            <i className="fas fa-layer-group" style={{ fontSize: 17 }} />
                        </div>
                        <div>
                            <h6
                                className="font-weight-bold text-dark mb-0"
                                style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}
                            >
                                Batch-wise Average Score Comparison
                            </h6>
                            <span className="text-muted" style={{ fontSize: 12.5 }}>
                                Subject &amp; chapter accuracy across your class &amp; stream — filter by section below
                            </span>
                        </div>
                    </div>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                        {/* No Branch filter — the header's branch selection scopes this comparison. */}
                        {sectionOptions.length > 1 && (
                            <div style={{ minWidth: 200 }} className="batch-filter-select">
                                <MultiSelect
                                    options={sectionOptions}
                                    value={sectionSel}
                                    onChange={(v) => this.setState({ batchSectionSel: v })}
                                    labelledBy="Sections"
                                    hasSelectAll
                                    valueRenderer={(selected) =>
                                        selected.length === 0
                                            ? "Select Sections"
                                            : selected.length === sectionOptions.length
                                                ? "All Sections"
                                                : `${selected.length} Sections selected`
                                    }
                                    overrideStrings={{
                                        allItemsAreSelected: "All Sections",
                                        selectSomeItems: "Select Sections",
                                        selectAll: "All Sections",
                                        search: "Search",
                                    }}
                                />
                            </div>
                        )}
                        {streamSections.length > 0 && (
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                {stream && (
                                    <span style={{ ...chipStyle, background: "#ede9fe", borderColor: "#ddd6fe", color: "#7c3aed" }}>
                                        <i className="fas fa-stream" style={{ fontSize: 11 }} /> {stream}
                                    </span>
                                )}
                                <span style={chipStyle}>
                                    <i className="fas fa-code-branch" style={{ fontSize: 11 }} /> {branchCount} Branch{branchCount > 1 ? "es" : ""}
                                </span>
                                <span style={chipStyle}>
                                    <i className="fas fa-users" style={{ fontSize: 11 }} /> {sectionCount} Section{sectionCount > 1 ? "s" : ""}
                                </span>
                                <span style={chipStyle}>
                                    <i className="fas fa-user-graduate" style={{ fontSize: 11 }} /> {totalStudents} Students
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ padding: "18px 20px" }}>
                    {/* Hide the batch graph when the STUDENT has no data for the
                        current filter (e.g. "Error Exam" but the student took none)
                        — otherwise the batch would show all-exam data misleadingly. */}
                    {(this.state.subjectPerformance || []).length === 0 ? (
                        <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: 240, fontSize: 14 }}>
                            <i className="fas fa-filter mb-2" style={{ fontSize: 26, color: "#cbd5e1" }} />
                            No exams for the selected filter — batch comparison isn't shown.
                        </div>
                    ) : scopedSections.length > 0 ? (
                        <>
                            <div style={{ borderRadius: 12, border: "1px solid #f1f2f5", background: "#fafaff", padding: "10px 12px" }}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={options}
                                    containerProps={{ style: { width: "100%" } }}
                                />
                            </div>
                            <div className="mt-4">
                                <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                                        <p
                                            className="font-weight-bold text-dark mb-0"
                                            style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13.5, letterSpacing: "0.2px" }}
                                        >
                                            <i className={`fas ${batchView === "heatmap" ? "fa-th" : "fa-table"} mr-2`} style={{ color: "#7c3aed" }} />
                                            {batchView === "heatmap" ? "Accuracy Heatmap" : "Chapter-wise Accuracy"}
                                        </p>

                                        <div style={{ minWidth: 170 }} className="batch-filter-select">
                                            <SelectDropDown
                                                stateData={this.state.batchSubjectSel || subjectFilterOptions[0]}
                                                handleChange={(name, value, opt) => this.setState({ batchSubjectSel: opt })}
                                                name="batchSubjectSel"
                                                options={subjectFilterOptions}
                                                placeholderName="Subject"
                                                dropdownIndicator={{ DropdownIndicator }}
                                            />
                                        </div>

                                    </div>
                                    <div className="sp-segment">
                                        <button type="button" className={batchView === "table" ? "active" : ""} onClick={() => this.setState({ batchView: "table" })}>
                                            <i className="fas fa-table" />Table
                                        </button>
                                        <button type="button" className={batchView === "heatmap" ? "active" : ""} onClick={() => this.setState({ batchView: "heatmap" })}>
                                            <i className="fas fa-th" />Heatmap
                                        </button>
                                    </div>
                                </div>
                                {batchView === "heatmap" ? (
                                    this.renderBatchHeatmap(scopedSections, selectedSubject, isAllowedSubject)
                                ) : (
                                    <CardLessDataTableWithSearch
                                        parentData={tableRows}
                                        particlecolumns={this.batchChapterColumns}
                                        name="report"
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center text-muted"
                            style={{ minHeight: 360, fontSize: 14 }}
                        >
                            <i
                                className={`fas ${streamSections.length > 0 ? "fa-filter" : "fa-chart-column"} mb-2`}
                                style={{ fontSize: 26, color: "#cbd5e1" }}
                            />
                            {streamSections.length > 0
                                ? "Select at least one branch / section to view the comparison."
                                : "No batch comparison available."}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Risk level → color (High = red, Medium = amber, Low = green).
    riskColor = (level) => {
        const l = `${level || ""}`.toLowerCase();
        if (l.includes("high")) return "#dc2626";
        if (l.includes("medium") || l.includes("moderate")) return "#d97706";
        if (l.includes("low")) return "#16a34a";
        return "#1e293b";
    };

    // ── MOU #4: Attempt Rate — attempted vs skipped in each test ──────────────
    // Surfaced directly on Overview (not buried in the results modal). Per test:
    // Attempted (correct+wrong) vs Not Answered (skipped), plus attempt-rate %.
    // Graph and Table views.
    // Build one combined row per exam: attempt/skip + score/rank/accuracy.
    // Online/Offline pill next to an exam name. Only rendered when a student has
    // both modes (single-mode students don't need per-row labels).
    renderModeBadge = (mode) => {
        const offline = mode === "offline";
        return (
            <span
                className="d-inline-flex align-items-center font-weight-bold ml-2"
                style={{
                    fontSize: 10, padding: "1px 7px", borderRadius: 10, verticalAlign: "middle",
                    background: offline ? "#eef2ff" : "#ecfdf5",
                    color: offline ? "#4f46e5" : "#059669",
                    border: `1px solid ${offline ? "#c7d2fe" : "#a7f3d0"}`,
                }}
            >
                <i className={`fas ${offline ? "fa-file-lines" : "fa-wifi"} mr-1`} style={{ fontSize: 8 }} />
                {offline ? "Offline" : "Online"}
            </span>
        );
    };

    // Section rank cell: "#3" badge + "of 42" so the standing is legible on its own.
    // Rank 1 = top scorer in the student's class + section for that exam.
    renderSectionRank = (r) => {
        const rank = r.section_rank;
        if (rank == null) return <span className="text-muted">-</span>;
        const total = r.section_total;
        return (
            <div className="d-inline-flex flex-column align-items-end">
                <span
                    className="d-inline-flex align-items-center justify-content-center text-white font-weight-bold"
                    style={{
                        minWidth: 30, height: 24, padding: "0 8px", borderRadius: 7,
                        background: this.rankColor(rank), fontSize: 12.5,
                    }}
                    title={`Rank ${rank}${total ? ` of ${total}` : ""} in your class & section for this exam (highest score first)`}
                >
                    {rank === 1 ? <i className="fas fa-crown mr-1" style={{ fontSize: 10 }} /> : null}
                    #{rank}
                </span>
                {total ? (
                    <span className="text-muted" style={{ fontSize: 10.5, marginTop: 2 }}>of {total}</span>
                ) : null}
            </div>
        );
    };

    buildExamPerformanceRows = () => {
        return (this.state.examWiseResult || [])
            // The college examWiseResult has no `attempted` field, so treat any exam
            // with questions (or a recorded score) as an attempt.
            .filter((e) => num(e.total_questions) > 0 || num(e.correct) > 0 || num(e.wrong) > 0)
            .map((e) => {
                const correct = num(e.correct);
                const wrong = num(e.wrong);
                const total = num(e.total_questions);
                const attempted = correct + wrong;
                const notAnswered = Math.max(0, num(e.not_answered) || (total - attempted));
                const rate = total ? Math.round((attempted / total) * 100) : 0;
                return {
                    id: e.id,
                    exam_name: e.exam_name || e.date || "Exam",
                    date: e.date || "",
                    correct, wrong, attempted,
                    not_answered: notAnswered,
                    total,
                    rate,
                    score: num(e.marks),
                    accuracy: e.accuracy != null ? num(e.accuracy) : null,
                    // Rank shown on Overview = SECTION rank, highest score first (rank 1 =
                    // section topper on that exam), among peers in the same institute,
                    // class and section. section_total = how many wrote it, so the rank
                    // renders as "3 of 42". exam_rank stays in the exam-wise modal only.
                    section_rank: e.section_rank != null ? num(e.section_rank) : null,
                    section_total: e.section_total != null ? num(e.section_total) : null,
                    // "online" | "offline" from exam_paper.exam_mode (0 vs 1/2).
                    mode: e.mode === "offline" ? "offline" : "online",
                };
            })
            .reverse(); // chronological oldest → newest
    };

    // ── MOU #4: Exam Performance — attempted vs skipped per test, COMBINED with
    // score/accuracy/rank, with an exam-paper dropdown (All / single) and
    // graph + table views. Surfaced directly on Overview. Handles any # of exams.
    //
    // Chart-options builder shared by the on-page (last-5) and popup (all-rows)
    // views. `rowsForChart` is oldest→newest; bars render in that order.
    buildAttemptChartOptions = (rowsForChart) => {
        const shown = rowsForChart || [];
        // Truncate long exam names on the axis; the FULL name lives in the tooltip.
        const categories = shown.map((r) => trunc(r.exam_name, 15));
        const fullNames = shown.map((r) => r.exam_name);
        const maxTotal = shown.reduce((mx, r) => Math.max(mx, r.total), 0);
        return {
            chart: { type: "column", backgroundColor: "transparent", height: 340 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                // <=4 exams stay horizontal (wide bars, readable); rotate at 5+.
                labels: { style: { color: "#64748b", fontWeight: "600", fontSize: "11px" }, rotation: shown.length > 4 ? -35 : 0 },
            },
            yAxis: [
                {
                    min: 0, max: maxTotal || null, endOnTick: false, maxPadding: 0,
                    title: { text: "Questions", style: { color: "#94a3b8", fontSize: "11px" } },
                    gridLineColor: "#eef2f6", labels: { style: { color: "#64748b" } },
                },
                {
                    min: 0, max: 100, opposite: true, gridLineWidth: 0,
                    title: { text: "Attempt %", style: { color: "#22c55e", fontSize: "11px" } },
                    labels: { format: "{value}%", style: { color: "#22c55e" } },
                },
            ],
            // Show the FULL exam name in the tooltip header (axis categories are
            // truncated to ~15 chars). We reuse the shared styled tooltip but wrap
            // its formatter so `this.key` reports the full name, not the truncated
            // category — keeps chartTooltip.js untouched.
            tooltip: (() => {
                const base = pointTooltip({ unitBySeries: { "Attempt %": "%" } });
                const inner = base.formatter;
                return {
                    ...base,
                    formatter: function () {
                        const full = fullNames[this.point && this.point.index != null ? this.point.index : this.x];
                        return inner.call({ ...this, key: full != null ? full : this.key });
                    },
                };
            })(),
            legend: { align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
            plotOptions: {
                column: {
                    stacking: "normal", borderRadius: 3, groupPadding: 0.15,
                    dataLabels: {
                        enabled: true,
                        // Hide labels for zero-height segments (a 0 stack label is
                        // what triggers the Highcharts hideOverlappingLabels crash).
                        formatter: function () { return this.y > 0 ? this.y : null; },
                        style: { fontWeight: "700", fontSize: "10px", color: "#fff", textOutline: "none" },
                    },
                },
                spline: { dataLabels: { enabled: true, formatter: function () { return this.y != null ? this.y + "%" : null; }, style: { fontWeight: "700", fontSize: "10px", color: "#16a34a", textOutline: "2px #fff" } } },
            },
            series: [
                { name: "Attempted", type: "column", yAxis: 0, data: shown.map((r) => r.attempted), color: "#22c55e" },
                { name: "Not Answered", type: "column", yAxis: 0, data: shown.map((r) => r.not_answered), color: "#f59e0b" },
                { name: "Attempt %", type: "spline", yAxis: 1, data: shown.map((r) => r.rate), color: "#16a34a", lineWidth: 3, marker: { enabled: true, radius: 4 } },
            ],
        };
    };

    // Column header shared by the on-page and popup tables.
    renderExamPerfTableHead = () => (
        <thead>
            <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Exam</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Correct</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Wrong</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Attempted</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Not Ans.</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Total</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Attempt %</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Score</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Accuracy</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }} title="Rank within your class & section in this institute">Section Rank</th>
                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Analysis</th>
            </tr>
        </thead>
    );

    // Table body rows shared by on-page (no aggregate) and popup (with aggregate).
    // `rows` is oldest→newest; rendered newest-first. `showModeFilter` toggles the
    // Online/Offline badge next to each exam name.
    renderExamPerfTableRows = (rows, { withAggregate = false, showModeFilter = false } = {}) => (
        <tbody>
            {/* "All exams" aggregate summary row — popup only. */}
            {withAggregate && rows.length > 1 && (() => {
                const agg = rows.reduce((a, r) => ({
                    correct: a.correct + r.correct, wrong: a.wrong + r.wrong,
                    attempted: a.attempted + r.attempted, not_answered: a.not_answered + r.not_answered,
                    total: a.total + r.total, score: a.score + r.score,
                }), { correct: 0, wrong: 0, attempted: 0, not_answered: 0, total: 0, score: 0 });
                const aggRate = agg.total ? Math.round((agg.attempted / agg.total) * 100) : 0;
                const aggAcc = agg.attempted ? Math.round((agg.correct / agg.attempted) * 100) : 0;
                return (
                    <tr style={{ borderBottom: "2px solid #eef2f6", background: "#f8fafc" }}>
                        <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>All Exams ({rows.length})</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#16a34a" }}>{agg.correct}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#ef4444" }}>{agg.wrong}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#16a34a" }}>{agg.attempted}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#d97706" }}>{agg.not_answered}</td>
                        <td className="text-right font-weight-bold text-dark" style={{ fontSize: 13 }}>{agg.total}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13, color: aggRate >= 90 ? "#16a34a" : aggRate >= 70 ? "#d97706" : "#dc2626" }}>{aggRate}%</td>
                        <td className="text-right font-weight-bold text-dark" style={{ fontSize: 13 }}>{agg.score}</td>
                        <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{aggAcc}%</td>
                        <td className="text-right text-muted" style={{ fontSize: 13 }}>—</td>
                        <td className="text-right text-muted" style={{ fontSize: 13 }}>—</td>
                    </tr>
                );
            })()}
            {rows.slice().reverse().map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                    <td style={{ fontSize: 13 }}>
                        <span className="font-weight-bold text-dark">{r.exam_name}</span>
                        {showModeFilter ? this.renderModeBadge(r.mode) : null}
                        {r.date ? <div className="text-muted" style={{ fontSize: 11 }}>{r.date}</div> : null}
                    </td>
                    <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#16a34a" }}>{r.correct}</td>
                    <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#ef4444" }}>{r.wrong}</td>
                    <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#16a34a" }}>{r.attempted}</td>
                    <td className="text-right font-weight-bold" style={{ fontSize: 13, color: "#d97706" }}>{r.not_answered}</td>
                    <td className="text-right text-muted" style={{ fontSize: 13 }}>{r.total}</td>
                    <td className="text-right font-weight-bold" style={{ fontSize: 13, color: r.rate >= 90 ? "#16a34a" : r.rate >= 70 ? "#d97706" : "#dc2626" }}>{r.rate}%</td>
                    <td className="text-right font-weight-bold text-dark" style={{ fontSize: 13 }}>{r.score}</td>
                    <td className="text-right" style={{ fontSize: 13 }}>{r.accuracy != null ? `${r.accuracy}%` : "-"}</td>
                    <td className="text-right" style={{ fontSize: 13 }}>{this.renderSectionRank(r)}</td>
                    <td className="text-right" style={{ fontSize: 13 }}>
                        <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
                            <button type="button" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center"
                                style={{ width: 30, height: 30, borderRadius: 8, padding: 0 }}
                                onClick={() => this.goToExamResult({ id: r.id, type: "schedule_exam" }, "schedule_exam")}
                                title="View result analysis">
                                <i className="fas fa-chart-line" style={{ fontSize: 12 }} />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                                style={{ width: 30, height: 30, borderRadius: 8, padding: 0 }}
                                onClick={() => this.goToViewQA({ id: r.id, type: "schedule_exam" }, "schedule_exam")}
                                title="View questions & answers">
                                <i className="fas fa-external-link-alt" style={{ fontSize: 12 }} />
                            </button>
                        </div>
                    </td>
                </tr>
            ))}
        </tbody>
    );

    // Filtered rows for the Exam Performance section, resolved once and reused by
    // the on-page view and the View-All popup. Returns oldest→newest.
    getExamPerfFiltered = () => {
        const allRows = this.buildExamPerformanceRows();
        const sel = this.state.attemptExamSel || { value: 0 };
        const isAll = !sel.value;
        // Online/Offline segmentation — only surfaced when the student has BOTH.
        const hasOnline = allRows.some((r) => r.mode === "online");
        const hasOffline = allRows.some((r) => r.mode === "offline");
        const showModeFilter = hasOnline && hasOffline;
        const modeSel = showModeFilter ? (this.state.attemptModeSel || "all") : "all";
        const modeFiltered = modeSel === "all" ? allRows : allRows.filter((r) => r.mode === modeSel);
        // Apply mode + exam-paper filter FIRST, then the on-page cap is taken later.
        const rows = isAll ? modeFiltered : modeFiltered.filter((r) => r.id === sel.value);
        return { rows, isAll, showModeFilter, modeSel };
    };

    renderExamPerformance = () => {
        const { rows, showModeFilter, modeSel } = this.getExamPerfFiltered();
        const view = this.state.attemptView;
        // ON-PAGE shows only the LAST 5 (most recent) of the filtered rows.
        const shown = rows.slice(-5);
        const total = rows.length;
        const hasMore = total > 5;
        const options = this.buildAttemptChartOptions(shown);

        return (
            <div className="p-3 h-100" style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}>
                <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            <i className="fas fa-list-check mr-2" style={{ color: "#7c3aed" }} />
                            Exam Performance — attempted vs skipped, score &amp; rank
                        </h6>
                        <span className="text-muted" style={{ fontSize: "11.5px" }}>
                            <span style={{ color: "#22c55e" }}>Attempted</span> = answered · <span style={{ color: "#f59e0b" }}>Not Answered</span> = left blank
                            {hasMore ? <span> · showing latest 5 of {total}</span> : null}
                        </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                        {/* Online/Offline filter — appears only when the student has both. */}
                        {showModeFilter && (
                            <div className="sp-segment sp-segment-sm">
                                <button type="button" className={modeSel === "all" ? "active" : ""} onClick={() => this.setState({ attemptModeSel: "all" })}>
                                    All
                                </button>
                                <button type="button" className={modeSel === "online" ? "active" : ""} onClick={() => this.setState({ attemptModeSel: "online" })}>
                                    <i className="fas fa-wifi" />Online
                                </button>
                                <button type="button" className={modeSel === "offline" ? "active" : ""} onClick={() => this.setState({ attemptModeSel: "offline" })}>
                                    <i className="fas fa-file-lines" />Offline
                                </button>
                            </div>
                        )}
                        {/* View All (N) — only when the filtered count exceeds the on-page 5. */}
                        {hasMore && (
                            <button type="button" className="btn btn-sm d-inline-flex align-items-center"
                                style={{ background: "#f4f0fe", color: "#7c3aed", fontWeight: 700, fontSize: 12, borderRadius: 8, border: "1px solid #e5d9fb", padding: "5px 12px" }}
                                onClick={() => this.setState({ examPerfModalOpen: true })}
                                title="View all attempted exams">
                                <i className="fas fa-up-right-and-down-left-from-center mr-2" style={{ fontSize: 11 }} />
                                View All ({total})
                            </button>
                        )}
                        {/* Exam-paper dropdown lives in the tab header now (top). */}
                        {shown.length > 0 && (
                            <div className="sp-segment sp-segment-sm">
                                <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ attemptView: "graph" })}>
                                    <i className="fas fa-chart-column" />Graph
                                </button>
                                <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ attemptView: "table" })}>
                                    <i className="fas fa-table" />Table
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {shown.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No attempted exams yet.</div>
                ) : view === "graph" ? (
                    // Capped at 5 on-page, so bars are always wide/readable. Keep the
                    // horizontal scroll wrapper for safety on narrow viewports.
                    <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                        <div style={{ minWidth: Math.max(shown.length * 80, 320) }}>
                            <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-borderless mb-0">
                            {this.renderExamPerfTableHead()}
                            {/* On-page: last-5, no aggregate row. */}
                            {this.renderExamPerfTableRows(shown, { withAggregate: false, showModeFilter })}
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // ── View-All popup: ALL filtered exams, scrollable graph + table with a
    // Graph/Table toggle. Reuses buildAttemptChartOptions + renderExamPerfTableRows.
    renderExamPerfModal = () => {
        if (!this.state.examPerfModalOpen) return null;
        const { rows, showModeFilter } = this.getExamPerfFiltered();
        const view = this.state.examPerfModalView || "table";
        const options = this.buildAttemptChartOptions(rows);
        return (
            <Modal show onHide={() => this.setState({ examPerfModalOpen: false })} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: "17px" }}>
                        <i className="fas fa-list-check mr-2" style={{ color: "#7c3aed" }} />
                        All Exams ({rows.length})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "72vh", overflowY: "auto" }}>
                    <div className="d-flex justify-content-end mb-3">
                        <div className="sp-segment sp-segment-sm">
                            <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ examPerfModalView: "graph" })}>
                                <i className="fas fa-chart-column" />Graph
                            </button>
                            <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ examPerfModalView: "table" })}>
                                <i className="fas fa-table" />Table
                            </button>
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No attempted exams yet.</div>
                    ) : view === "graph" ? (
                        // Horizontal scroll at ~80px/bar so many exams stay readable.
                        <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                            <div style={{ minWidth: Math.max(rows.length * 80, 320) }}>
                                <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-borderless mb-0">
                                {this.renderExamPerfTableHead()}
                                {/* Popup: ALL rows, WITH the aggregate summary row. */}
                                {this.renderExamPerfTableRows(rows, { withAggregate: true, showModeFilter })}
                            </table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    // The exam the header Exam-Paper dropdown has selected (0 / null = All).
    // Returns the matching examWiseResult row so every section can scope to it.
    getSelectedExam = () => {
        const sel = this.state.attemptExamSel;
        if (!sel || !sel.value) return null; // All exams
        return (this.state.examWiseResult || []).find((e) => e.id === sel.value) || null;
    };

    render() {
        const { overallData, subjectPerformance, snapshot, batchComparison, loading } = this.state;

        // When a single exam is selected in the header, scope the WHOLE tab to it:
        // summary cards, trend chart and snapshot all reflect that one exam. When
        // "All exams" is selected, use the full backend datasets as-is.
        const selectedExam = this.getSelectedExam();
        const isSingle = !!selectedExam;

        // Filtered exam-subject matrix (single exam → just that paper's row).
        const filteredMatrix = isSingle
            ? (this.state.examSubjectMatrix || []).filter(
                (m) => m.exam_name === selectedExam.exam_name || String(m.session_id) === String(selectedExam.id))
            : (this.state.examSubjectMatrix || []);

        // Cards + trend read from these; when single, build a one-row "overall".
        const scopedOverall = isSingle
            ? [{
                exam_name: selectedExam.exam_name,
                total_attempt_test: 1,
                average_score: num(selectedExam.marks),
                highest_score: num(selectedExam.marks),
                lowest_score: num(selectedExam.marks),
                section_avg_score: 0,
                branch_avg_score: 0,
                avg_rank: selectedExam.exam_rank != null ? num(selectedExam.exam_rank) : 0,
                accuracy: selectedExam.accuracy != null ? num(selectedExam.accuracy) : 0,
            }]
            : overallData;

        return (
            <TabSection
                icon="fa-eye"
                title="Overview"
                description="Consolidated snapshot of the student's exam performance."
                headerRight={this.renderControls()}
            >
                {loading ? (
                    <PageShimmer />
                ) : (
                    <Row>
                        {/* Filter banner when a single exam is scoped. */}
                        {isSingle && (
                            <Col sm={12} className="mb-2">
                                <div className="d-flex align-items-center justify-content-between flex-wrap px-3 py-2" style={{ gap: 8, background: "#f4f0fe", borderRadius: 10 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: "#4b3a72" }}>
                                        <i className="fas fa-filter mr-2" />Showing: <b>{selectedExam.exam_name}</b>
                                    </span>
                                    <button type="button" className="btn btn-sm btn-link p-0" style={{ fontSize: 12, color: "#7c3aed", fontWeight: 700 }}
                                        onClick={() => this.setState({ attemptExamSel: { value: 0, label: "All exams" } })}>
                                        Clear — show all exams
                                    </button>
                                </div>
                            </Col>
                        )}

                        <Col sm={12}><ChartBoundary resetKey={this.state.attemptExamSel} label="Summary unavailable for this selection.">{this.renderSummaryCards(scopedOverall, filteredMatrix)}</ChartBoundary></Col>

                        {/* Trend is inherently multi-exam (score movement across
                            tests), so it ALWAYS uses the full data — a single-exam
                            filter would collapse it to one meaningless point. */}
                        <Col sm={6}><ChartBoundary resetKey={this.state.attemptExamSel} label="Trend chart unavailable.">{this.renderTrendChart(overallData, selectedExam)}</ChartBoundary></Col>

                        <Col sm={6}><ChartBoundary label="Snapshot unavailable.">{this.renderSnapshot(snapshot)}</ChartBoundary></Col>

                        {/* MOU #4 — Exam Performance: attempted vs skipped + score/rank
                            per exam. Combines the old summary table + attempt data,
                            controlled by the header Exam-Paper dropdown. Graph/table. */}
                        <Col sm={12}>
                            <div className="mt-4"><ChartBoundary resetKey={this.state.attemptExamSel} label="Exam performance unavailable.">{this.renderExamPerformance()}</ChartBoundary></div>
                        </Col>

                        {/* Exam History table commented out — its row actions now live in
                            the Schedule Exam "Exam-wise Results" modal (modalColumns). */}
                        {/* <Col sm={12}>{this.renderExamHistoryTable()}</Col> */}

                        <Col sm={12}>
                            {this.state.batchLoading ? (
                                this.renderBatchLoading()
                            ) : (
                                <ChartBoundary resetKey={this.state.attemptExamSel} label="Batch comparison unavailable.">{this.renderBatchComparison(batchComparison)}</ChartBoundary>
                            )}
                        </Col>
                    </Row>
                )}
                {this.renderExamModal()}
                {this.renderExamPerfModal()}
                {this.renderBatchDetailModal()}
                {this.renderChapterTopicModal()}
                {this.renderSubjectMatrixModal()}
                {this.renderSummaryModal()}
                {this.renderTopStudentsDrawer()}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(OverviewTab);
